import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";

import PropTypes from "prop-types";
import { useLocation, useNavigate } from "react-router-dom";

import Card from "@mui/material/Card";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Tooltip from "@mui/material/Tooltip";
import { ContentState, EditorState } from "draft-js";
import { Editor } from "react-draft-wysiwyg";
import { convertFromHTML, convertToHTML } from "draft-convert";
import * as XLSX from "xlsx";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftSelect from "components/SoftSelect";
import SoftSnackbar from "components/SoftSnackbar";
import SoftTypography from "components/SoftTypography";
import { API_URL } from "config";
import { useAuth } from "../../../AuthContext";
import FlagLoader from "layouts/flagloader";
import { apiErrorMessage, apiSuccessMessage, readJsonSafely } from "utils/apiResponseMessages";
import { useModulePermissions } from "utils/modulePermissions";
import { formatCountryOptionLabel } from "utils/countryOptions";

const emptyDetail = {
  country: null,
  targeting: "",
  loi: "",
  ir: "",
  required_n: "",
  bid_cpi: "",
  won_cpi: "",
  feasibility: "",
  required_field_time_days: "",
};

const BID_MAX_ATTACHMENTS = 5;
const BID_MAX_ATTACHMENT_SIZE_BYTES = 30 * 1024 * 1024;
const BID_ATTACHMENT_ACCEPT = [
  "image/*",
  ".pdf",
  ".doc",
  ".docx",
  ".xls",
  ".xlsx",
  ".csv",
].join(",");
const BID_ATTACHMENT_EXTENSIONS = ["pdf", "doc", "docx", "xls", "xlsx", "csv"];
const BID_TARGETING_MAX_WORDS = 100;

const digitsOnly = (value, maxDigits) => String(value || "").replace(/\D/g, "").slice(0, maxDigits);

const integerInput = (value, maxDigits) => {
  const integerPart = String(value ?? "").split(".")[0];

  return integerPart.replace(/\D/g, "").slice(0, maxDigits);
};

const decimalInput = (value, integerDigits, decimalDigits) => {
  const cleaned = String(value || "").replace(/[^\d.]/g, "");
  const [integer = "", ...decimalParts] = cleaned.split(".");
  const nextInteger = integer.slice(0, integerDigits);
  const decimal = decimalParts.join("").slice(0, decimalDigits);

  if (cleaned.includes(".")) {
    if (!nextInteger && decimal) return `0.${decimal}`;

    return `${nextInteger}${nextInteger ? "." : ""}${decimal}`;
  }

  return nextInteger;
};

const trimTrailingDecimal = (value) => String(value ?? "").replace(/\.$/, "");

const valueOrFallback = (value, fallback = "") =>
  value === null || value === undefined || value === "" ? fallback : value;

const plainWordCount = (text) => {
  const matches = String(text || "").trim().match(/[\p{L}\p{N}]+(?:['-][\p{L}\p{N}]+)*/gu);

  return matches ? matches.length : 0;
};

const createEmptyForm = () => ({
  id: null,
  status: "",
  project_title: "",
  project_manager_role: null,
  project_manager: null,
  project_type: null,
  project_type_option: null,
  client: null,
  project_launch_date: "",
  attachments: [],
  existing_attachments: [],
  comments: "",
  details: [{ ...emptyDetail }],
});

const editableBidStatuses = ["in progress", "hold"];

const createWonForm = () => ({
  project_title: "",
  project_manager_role: null,
  project_manager: null,
  project_type: null,
  project_type_option: null,
  client: null,
  project_launch_date: "",
  comments: "",
});

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const toDateTimeLocalInput = (value) => {
  if (!value) return "";
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return String(value).replace(" ", "T").slice(0, 16);
  }

  const pad = (item) => String(item).padStart(2, "0");

  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
    date.getHours()
  )}:${pad(date.getMinutes())}`;
};

const dateOnlyValue = (value) => {
  if (!value) return "";

  const text = String(value);
  const directDate = text.match(/^(\d{4}-\d{2}-\d{2})/);
  if (directDate) return directDate[1];

  const date = new Date(text.replace(" ", "T"));
  if (Number.isNaN(date.getTime())) return "";

  const pad = (item) => String(item).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

const dateTimeFilterValue = (value) => {
  if (!value) return "";

  const text = String(value).trim();
  const localText = /^\d{4}-\d{2}-\d{2}$/.test(text)
    ? `${text}T00:00:00`
    : text.replace(" ", "T");
  const date = new Date(localText);

  if (Number.isNaN(date.getTime())) return "";

  const pad = (item) => String(item).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
    date.getHours()
  )}:${pad(date.getMinutes())}`;
};

const textOrDash = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  return value;
};

const formatIrDisplay = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  const number = Number(value);
  if (!Number.isFinite(number)) return value;
  const whole = Math.floor(number);
  return number - whole > 0.5 ? whole + 1 : whole;
};

const apiBaseUrl = API_URL.replace(/\/api\/?$/i, "");

const normalizeAttachments = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value.filter(Boolean);
  if (typeof value === "object") return [value];

  const text = String(value).trim();
  if (!text) return [];

  try {
    const parsed = JSON.parse(text);
    if (Array.isArray(parsed)) return parsed.filter(Boolean);
    if (parsed && typeof parsed === "object") return [parsed];
  } catch (error) {
    // Plain text attachment labels are still valid display values.
  }

  return text.includes(",")
    ? text
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean)
    : [text];
};

const attachmentLabel = (attachment, index) => {
  if (attachment && typeof attachment === "object") {
    const label =
      attachment.name ||
      attachment.file_name ||
      attachment.original_name ||
      attachment.title ||
      attachment.label ||
      attachment.path ||
      attachment.url;
    if (label) return String(label).split(/[\\/]/).pop();
  }

  return String(attachment || `Attachment ${index + 1}`).split(/[\\/]/).pop();
};

const attachmentPath = (attachment) => {
  if (attachment && typeof attachment === "object") {
    return (
      attachment.url ||
      attachment.download_url ||
      attachment.file_url ||
      attachment.path ||
      attachment.file_path ||
      attachment.document_path ||
      ""
    );
  }

  return String(attachment || "");
};

const attachmentUrl = (attachment) => {
  const path = attachmentPath(attachment).trim();
  if (!path) return "";
  if (/^(https?:)?\/\//i.test(path)) return path;
  if (!/[\\/]/.test(path) && !/\.(pdf|docx?|xlsx?|png|jpe?g|gif|webp|txt)$/i.test(path)) return "";

  const cleanPath = path.replace(/^\/+/, "").replace(/\\/g, "/");
  const storagePath = cleanPath.startsWith("storage/") ? cleanPath : `storage/${cleanPath}`;

  return `${apiBaseUrl}/${storagePath}`;
};

const attachmentIcon = (label) => {
  const extension = String(label || "").split(".").pop().toLowerCase();

  if (extension === "pdf") return "picture_as_pdf";
  if (["png", "jpg", "jpeg", "gif", "webp", "bmp"].includes(extension)) return "image";
  if (["doc", "docx"].includes(extension)) return "description";
  if (["xls", "xlsx", "csv"].includes(extension)) return "table_chart";

  return "attach_file";
};

const attachmentNames = (files) => {
  if (!files?.length) return "No file chosen";
  if (files.length === 1) return files[0].name;

  return `${files.length} files selected`;
};

const renderAttachmentLinks = (value) => {
  const attachments = normalizeAttachments(value);

  if (!attachments.length) {
    return (
      <SoftTypography variant="body2" color="text">
        -
      </SoftTypography>
    );
  }

  return (
    <SoftBox display="flex" alignItems="center" flexWrap="wrap" gap={0.75}>
      {attachments.map((attachment, index) => {
        const label = attachmentLabel(attachment, index);
        const url = attachmentUrl(attachment);
        const icon = attachmentIcon(label);
        const key = `${label}-${index}`;

        return (
          <Tooltip key={key} title={url ? `Open ${label}` : label}>
            {url ? (
              <IconButton
                component="a"
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                size="small"
                sx={{ color: "#0ea5e9", border: "1px solid #d8e4ef", borderRadius: "8px" }}
              >
                <Icon fontSize="small">{icon}</Icon>
              </IconButton>
            ) : (
              <IconButton
                size="small"
                disabled
                sx={{ border: "1px solid #d8e4ef", borderRadius: "8px" }}
              >
                <Icon fontSize="small">{icon}</Icon>
              </IconButton>
            )}
          </Tooltip>
        );
      })}
    </SoftBox>
  );
};

const editorStateFromHtml = (html) => {
  if (!html) return EditorState.createEmpty();

  try {
    return EditorState.createWithContent(convertFromHTML(html));
  } catch (error) {
    return EditorState.createWithContent(ContentState.createFromText(String(html)));
  }
};

const sanitizeRichHtml = (html) => {
  if (!html) return "";
  if (typeof window === "undefined" || !window.DOMParser) return String(html);

  const parser = new window.DOMParser();
  const doc = parser.parseFromString(String(html), "text/html");

  doc.querySelectorAll("script, style, iframe, object, embed").forEach((node) => node.remove());
  doc.body.querySelectorAll("*").forEach((node) => {
    [...node.attributes].forEach((attribute) => {
      const name = attribute.name.toLowerCase();
      const value = attribute.value.trim().toLowerCase();

      if (name.startsWith("on") || value.startsWith("javascript:")) {
        node.removeAttribute(attribute.name);
      }
    });
  });

  return doc.body.innerHTML;
};

const richTextDisplaySx = {
  "&, & p": {
    fontSize: "10px",
    fontWeight: 400,
    lineHeight: 1.15,
    margin: 0,
  },
  "& ul, & ol": {
    marginTop: 0,
    marginBottom: 0,
    paddingLeft: "18px",
  },
  "& a": {
    color: "#0ea5e9",
  },
};

const targetingEditorSx = {
  border: "1px solid #d2d6da",
  borderRadius: "6px",
  overflow: "hidden",
  backgroundColor: "#fff",
  "& .rdw-editor-toolbar": {
    border: "0",
    borderBottom: "1px solid #e2e8f0",
    marginBottom: 0,
    padding: "1px 3px",
    minHeight: 24,
  },
  "& .rdw-option-wrapper": {
    minWidth: 20,
    height: 20,
    margin: "0 1px",
  },
  "& .rdw-dropdown-wrapper": {
    height: 22,
    margin: "0 1px",
  },
  "& .rdw-editor-main": {
    minHeight: 108,
    maxHeight: 162,
    overflowY: "auto",
    padding: "4px 8px",
    fontSize: "10px",
    lineHeight: 1.15,
  },
  "& .public-DraftEditor-content": {
    minHeight: 59,
  },
};

function TargetingEditor({ value, onChange, readOnly = false, maxWords = BID_TARGETING_MAX_WORDS }) {
  const lastHtmlRef = useRef(value || "");
  const [editorState, setEditorState] = useState(() => editorStateFromHtml(value));

  useEffect(() => {
    const nextValue = value || "";
    if (nextValue !== lastHtmlRef.current) {
      setEditorState(editorStateFromHtml(nextValue));
      lastHtmlRef.current = nextValue;
    }
  }, [value]);

  const handleEditorChange = (nextState) => {
    if (readOnly) return;

    const content = nextState.getCurrentContent();
    if (maxWords && plainWordCount(content.getPlainText(" ")) > maxWords) return;

    setEditorState(nextState);

    const html = content.hasText() ? convertToHTML(content) : "";
    lastHtmlRef.current = html;
    onChange(html);
  };

  return (
    <SoftBox sx={targetingEditorSx}>
      <Editor
        editorState={editorState}
        onEditorStateChange={handleEditorChange}
        readOnly={readOnly}
        toolbar={{
          options: ["inline", "fontSize", "colorPicker", "list", "link", "remove", "history"],
          inline: { options: ["bold", "italic", "underline"] },
          list: { options: ["unordered", "ordered"] },
          link: { options: ["link", "unlink"] },
          fontSize: { options: [8, 9, 10, 11, 12, 14, 16, 18, 24] },
        }}
        toolbarClassName="targeting-editor-toolbar"
        editorClassName="targeting-editor-body"
      />
    </SoftBox>
  );
}

TargetingEditor.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  readOnly: PropTypes.bool,
  maxWords: PropTypes.number,
};

const normalizedStatus = (status) => String(status || "in progress").toLowerCase();

const statusTabs = [
  { value: "in progress", label: "In Progress" },
  { value: "vendor feasibility", label: "Vendors Feasibility" },
  { value: "hold", label: "Hold" },
  { value: "won", label: "Won" },
  { value: "canceled", label: "Canceled" },
];

const statusTabValues = statusTabs.map((tab) => tab.value);

const statusTabFromSearch = (search) => {
  const tab = new URLSearchParams(search || "").get("tab");
  return statusTabValues.includes(tab) ? tab : "in progress";
};

const permissionSegment = (value) => String(value || "").toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");

const statusTabPermissionKey = (value) => `bids.tabs.${permissionSegment(value)}.view`;
const BID_LIST_PAGE_SIZE = 50;

// const statusTabStyles = {
//   "in progress": { color: "#0ea5e9", background: "#e0f2fe", border: "#7dd3fc" },
//   "vendor feasibility": { color: "#6366f1", background: "#eef2ff", border: "#c7d2fe" },
//   hold: { color: "#f6a800", background: "#fff7df", border: "#ffd666" },
//   won: { color: "#16a34a", background: "#dcfce7", border: "#86efac" },
//   canceled: { color: "#ff4d6d", background: "#ffe8ee", border: "#ffb3c1" },
// };

// const statusActionMeta = {
//   "in progress": { label: "In Progress", color: "#0ea5e9", buttonColor: "info" },
//   "vendor feasibility": { label: "Vendor Feasibility", color: "#6366f1", buttonColor: "success" },
//   hold: { label: "Hold", color: "#f6a800", buttonColor: "warning" },
//   won: { label: "Won", color: "#16a34a", buttonColor: "success" },
//   canceled: { label: "Canceled", color: "#ff4d6d", buttonColor: "error" },
// };

const statusTabStyles = {
  "in progress": { color: "#0ea5e9", background: "#e0f2fe", border: "#7dd3fc" },
  // Inactive par ab yeh light steel-blue tint dega, green jesa bilkul nahi lagega
  "vendor feasibility": { color: "#0d9488", background: "#ecf2f7", border: "#ccdbe8" },
  hold: { color: "#f6a800", background: "#fff7df", border: "#ffd666" },
  won: { color: "#16a34a", background: "#dcfce7", border: "#86efac" },
  canceled: { color: "#ff4d6d", background: "#ffe8ee", border: "#ffb3c1" },
};

const statusActionMeta = {
  "in progress": { label: "In Progress", color: "#0ea5e9", buttonColor: "info" },
  // Active hone par wahi dark teal text/button background uthayega jo image_afad04.jpg mein hai
  "vendor feasibility": { label: "Vendor Feasibility", color: "#0d9488", buttonColor: "info" },
  hold: { label: "Hold", color: "#f6a800", buttonColor: "warning" },
  won: { label: "Won", color: "#16a34a", buttonColor: "success" },
  canceled: { label: "Canceled", color: "#ff4d6d", buttonColor: "error" },
};

const isBidInStatusTab = (bid, tabValue) => {
  const status = normalizedStatus(bid.status);
  const projectStatuses = [
    "soft_launch",
    "full_launch",
    "hard_launch",
    "live",
    "pause",
    "invoiced",
    "invoice_at_zero",
    "closed",
  ];

  if (tabValue === "in progress") {
    return !["hold", "won", "canceled", ...projectStatuses].includes(status);
  }

  return status === tabValue;
};

const toOption = (item) => ({
  value: item.value ?? item.id,
  label: item.label || item.name,
  name: item.name || item.label,
  code: item.iso_code || item.code || "",
  iso_code: item.iso_code || item.code || "",
  email: item.email || "",
  users: item.users || [],
});

const stripClientCodeFromLabel = (label = "") =>
  String(label || "").replace(/\s*\([^)]*\)\s*$/, "").trim();

const clientDisplayName = (item) => {
  const name =
    item?.client_name ||
    item?.client?.name ||
    item?.name ||
    item?.company_name ||
    stripClientCodeFromLabel(item?.label);

  return name || stripClientCodeFromLabel(item?.label);
};

const toClientOption = (item) => {
  const name = clientDisplayName(item);

  return {
    ...toOption(item),
    label: name,
    name,
  };
};

const clientOptionFromBid = (bid) => {
  const value = bid?.client_id || bid?.client?.id;
  if (!value) return null;

  const name = clientDisplayName(bid);

  return { value, label: name, name };
};

const projectManagerOptionFromBid = (bid) => {
  if (bid?.project_manager?.id) return toOption(bid.project_manager);

  return null;
};

const projectManagerRoleOptionFromBid = (bid) => {
  if (bid?.project_manager_role?.id) return toOption(bid.project_manager_role);

  return null;
};

const projectTypeOptionFromBid = (bid) => {
  const value = bid?.project_type_id || bid?.project_type_master?.id;
  const label = bid?.project_type_master?.name || bid?.project_type || "";

  return value && label ? { value, label, name: label } : null;
};

const bidProjectTypeOptionFromBid = (bid) => {
  const option = typeof bid?.project_type_option === "object" ? bid.project_type_option : null;
  const value = bid?.project_type_option_id || option?.id;
  const label = option?.name || option?.label || (typeof bid?.project_type_option === "string" ? bid.project_type_option : "");

  return value && label ? { value, label, name: label } : null;
};

const mergeSelectedOption = (options, selected) => {
  if (!selected?.value) return options;

  return [
    selected,
    ...options.filter((option) => String(option.value) !== String(selected.value)),
  ];
};

const listStudyTypeOptionFromBid = (bid) => {
  const label = bid?.project_type_master?.name || bid?.project_type || "";
  const value = bid?.project_type_id || bid?.project_type_master?.id || label;

  return value && label ? { value: String(value), label: String(label), name: String(label) } : null;
};

const listProjectTypeOptionFromBid = (bid) => {
  const option = typeof bid?.project_type_option === "object" ? bid.project_type_option : null;
  const label =
    option?.name || option?.label || (typeof bid?.project_type_option === "string" ? bid.project_type_option : "");
  const value = bid?.project_type_option_id || option?.id || label;

  return value && label ? { value: String(value), label: String(label), name: String(label) } : null;
};

const listClientOptionFromBid = (bid) => {
  const label = clientDisplayName(bid);
  const value = bid?.client_id || bid?.client?.id || label;

  return value && label ? { value: String(value), label: String(label), name: String(label) } : null;
};

const selectedListOptionMatches = (selected, rowOption) => {
  if (!selected?.value) return true;
  if (!rowOption) return false;

  const selectedValue = String(selected.value);
  const selectedLabel = String(selected.label || selected.name || "").toLowerCase();
  const rowValue = String(rowOption.value || "");
  const rowLabel = String(rowOption.label || rowOption.name || "").toLowerCase();

  return rowValue === selectedValue || (selectedLabel && rowLabel === selectedLabel);
};

const selectedListOptionsMatch = (selected, rowOption) => {
  const selections = Array.isArray(selected)
    ? selected
    : selected?.value
      ? [selected]
      : [];

  if (!selections.length) return true;

  return selections.some((option) => selectedListOptionMatches(option, rowOption));
};

const uniqueSortedListOptions = (rows, optionFromRow) => {
  const optionsByValue = new Map();

  rows.forEach((row) => {
    const option = optionFromRow(row);
    if (!option) return;

    const key = option.value || option.label.toLowerCase();
    if (!optionsByValue.has(key)) {
      optionsByValue.set(key, option);
    }
  });

  return Array.from(optionsByValue.values()).sort((first, second) =>
    String(first.label).localeCompare(String(second.label))
  );
};

const statusTabLabel = (value) =>
  statusTabs.find((tab) => tab.value === value)?.label ||
  statusActionMeta[value]?.label ||
  textOrDash(value);

const exportFilenamePart = (value) =>
  String(value || "bids")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const countryDetailId = (detail) => detail?.country_id || detail?.country?.id;

const countryDisplayValue = (detail) => {
  return detail?.country_name || detail?.country?.name || "";
};

const countryOptionFromDetail = (detail) => {
  const value = countryDetailId(detail);
  const label = countryDisplayValue(detail);
  const code = detail?.country?.iso_code || detail?.country?.code || detail?.country_code || "";

  return value && label ? { value, label, name: label, code, iso_code: code } : null;
};

const stripVendorCodeFromLabel = (label = "") =>
  String(label || "")
    .replace(/\s*\([^)]*\)/g, "")
    .replace(/\s+,/g, ",")
    .replace(/\s{2,}/g, " ")
    .trim();

const vendorDisplayName = (item) =>
  item?.name || item?.company_name || stripVendorCodeFromLabel(item?.label || item?.listing_vendor);

const vendorFeasibilityKey = (vendorFeasibility) =>
  String(vendorFeasibility?.invite_id || vendorFeasibility?.row_key || vendorFeasibility?.listing_vendor || "");

const vendorFeasibilityDetailKey = (vendorFeasibility, detail) =>
  `${vendorFeasibility?.invite_id || vendorFeasibility?.row_key || ""}-${detail?.id || ""}`;

const vendorFeasibilityVendorId = (vendorFeasibility) => {
  const vendor = vendorFeasibility?.vendor || {};
  return vendor.value || vendor.id || vendorFeasibility?.vendor_id;
};

const vendorFeasibilityUnwonDetails = (vendorFeasibility) =>
  (Array.isArray(vendorFeasibility?.details) ? vendorFeasibility.details : []).filter(
    (detail) => detail?.id && !detail.country_won
  );

const listingVendorText = (bid) =>
  stripVendorCodeFromLabel(
    bid?.listing_vendor ||
      bid?.vendor_feasibility_vendor?.name ||
      bid?.vendor_feasibility_vendor?.company_name ||
      ""
  );

const vendorFeasibilityCountText = (bid) => {
  const count = Number(bid?.vendor_feasibility_count);

  if (Number.isFinite(count) && count >= 0) return String(count);

  if (Array.isArray(bid?.vendor_feasibilities)) return String(bid.vendor_feasibilities.length);

  return listingVendorText(bid);
};

const vendorToOption = (item) => {
  const name = vendorDisplayName(item);

  return {
    value: item.value ?? item.id,
    label: name,
    name,
    code: item.code || "",
    email: item.email || "",
  };
};

const fieldActionButtonSx = {
  width: 40,
  height: 40,
  border: "1px solid #d2d6da",
  borderRadius: "8px",
  flex: "0 0 40px",
};

const compactFieldActionButtonSx = {
  ...fieldActionButtonSx,
  width: 28,
  minWidth: 28,
  height: 28,
  minHeight: 28,
  flex: "0 0 28px",
  borderRadius: "6px",
  p: 0,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  lineHeight: 1,
  "& .MuiIcon-root, & .material-icons": {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "1rem",
    height: "1rem",
    fontSize: "1rem !important",
    lineHeight: "1 !important",
    margin: "0 !important",
  },
};

const tabSizedButtonSx = {
  minWidth: "auto",
  width: "fit-content",
  height: 28,
  minHeight: 28,
  px: 2,
  py: "0 !important",
  mb: 0,
  fontSize: "0.875rem",
  fontWeight: 400,
  lineHeight: "28px",
  textTransform: "none",
  whiteSpace: "nowrap",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  "& .MuiButton-startIcon": {
    alignItems: "center",
    marginTop: 0,
    marginBottom: 0,
  },
  "& .MuiButton-startIcon .MuiIcon-root, & .MuiButton-startIcon .material-icons": {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    lineHeight: "1 !important",
    margin: "0 !important",
  },
};

const compactTableButtonSx = {
  ...tabSizedButtonSx,
  height: "28px !important",
  minHeight: "28px !important",
  px: "12px !important",
  py: "0 !important",
  fontSize: "12px !important",
  lineHeight: "28px !important",
  borderRadius: "6px !important",
};

const compactTextareaSx = {
  "& textarea": {
    resize: "vertical",
    height: "28px !important",
    minHeight: "28px !important",
    padding: "0 8px !important",
    lineHeight: "28px !important",
    overflow: "hidden",
  },
};

const compactBidDialogTitleSx = {
  fontSize: "16px",
  fontWeight: 400,
  lineHeight: 1.2,
  px: 2.5,
  pt: 1,
  pb: 0.25,
};

const compactBidDialogContentSx = {
  "--soft-select-height": "28px",
  "--soft-select-value-height": "26px",
  "--soft-select-font-size": "10px",
  "--soft-select-value-px": "8px",
  "--soft-select-text-top": "50%",
  "--soft-select-indicator-padding": "4px",
  "--soft-select-indicator-size": "12px",
  "--soft-select-menu-padding": "4px",
  "--soft-select-option-padding": "4px 8px",
  "--soft-select-border-radius": "6px",
  px: 2.5,
  pt: "0px !important",
  pb: 0.35,
  "& .MuiTypography-root": {
    fontSize: "10px",
    lineHeight: 1.1,
  },
  "& .MuiInputBase-root": {
    height: "28px !important",
    minHeight: "28px !important",
    borderRadius: "6px !important",
    fontSize: "10px !important",
    display: "flex !important",
    alignItems: "center !important",
    boxSizing: "border-box !important",
    padding: "0 !important",
    mb: 0,
  },
  "& .MuiInputBase-input": {
    height: "28px !important",
    minHeight: "28px !important",
    padding: "0 8px !important",
    fontSize: "10px !important",
    lineHeight: "28px !important",
    boxSizing: "border-box !important",
  },
  "& input[type='datetime-local'].MuiInputBase-input": {
    display: "flex !important",
    alignItems: "center !important",
    lineHeight: "28px !important",
  },
  "& input[type='datetime-local']::-webkit-calendar-picker-indicator": {
    width: "14px",
    height: "14px",
    padding: 0,
    margin: 0,
  },
  "& .MuiInputBase-root.MuiInputBase-multiline": {
    padding: "0 !important",
    alignItems: "center !important",
  },
  "& textarea.MuiInputBase-input": {
    height: "28px !important",
    minHeight: "28px !important",
    padding: "0 8px !important",
    lineHeight: "28px !important",
    overflow: "hidden !important",
  },
  "& [class*='-control']": {
    height: "28px !important",
    minHeight: "28px !important",
    borderRadius: "6px !important",
    fontSize: "10px !important",
  },
  "& [class*='-ValueContainer']": {
    height: "26px !important",
    padding: "0 8px !important",
    fontSize: "10px !important",
  },
  "& [class*='-placeholder'], & [class*='-singleValue'], & [class*='-Input']": {
    fontSize: "10px !important",
    fontFamily: '"Poppins", "Helvetica", "Arial", sans-serif !important',
    fontWeight: "400 !important",
    lineHeight: "1.15 !important",
    color: "#334155 !important",
  },
  "& [class*='-IndicatorsContainer']": {
    height: "26px !important",
  },
  "& [class*='-indicatorContainer']": {
    padding: "4px !important",
  },
};

const compactAttachmentBoxSx = {
  border: "1px dashed #cbd5e1",
  borderRadius: "6px",
  height: 28,
  px: 0.5,
  py: 0,
  backgroundColor: "#f8fafc",
  "& .MuiTypography-root": {
    lineHeight: "28px",
  },
};

const compactBidDialogActionsSx = {
  px: 2.5,
  pt: 0.25,
  pb: 0.75,
};

const responsiveBidDialogPaperSx = {
  width: { xs: "96vw", sm: "92vw", md: "82vw", lg: "70vw" },
  height: { xs: "92dvh", sm: "88dvh", md: "85vh" },
  maxWidth: "none",
  maxHeight: { xs: "calc(100dvh - 16px)", sm: "calc(100dvh - 32px)" },
  padding: { xs: "0 6px", sm: "0px 10px" },
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  "& .MuiDialogTitle-root, & .MuiDialogActions-root": {
    flexShrink: 0,
  },
  "& .MuiDialogContent-root": {
    flex: 1,
    overflowY: "auto",
  },
};

const compactSoftSelectProps = { compact: true };

const listSearchInputSx = {
  minHeight: "24px !important",
  padding: "1px 8px !important",
  "& input": {
    height: "20px !important",
    fontSize: "10px !important",
  },
  "& input[type='date']": {
    lineHeight: "20px !important",
  },
  "& input[type='date']::-webkit-calendar-picker-indicator": {
    width: "12px",
    height: "12px",
    padding: 0,
    margin: 0,
  },
};

const listSearchControlSx = {
  width: { xs: "100%", sm: "9rem" },
  flex: { xs: "1 1 100%", sm: "0 0 9rem" },
};

const listSearchSelectSx = {
  "--soft-select-height": "24px",
  "--soft-select-value-height": "22px",
  "--soft-select-font-size": "10px",
  "--soft-select-value-px": "8px",
  "--soft-select-text-top": "50%",
  "--soft-select-indicator-padding": "3px",
  "--soft-select-indicator-size": "12px",
  "--soft-select-menu-padding": "4px",
  "--soft-select-option-padding": "4px 8px",
  "--soft-select-border-radius": "6px",
};

const listExportButtonSx = {
  minHeight: 24,
  height: 24,
  px: 1.25,
  py: 0,
  fontSize: "10px",
  lineHeight: 1,
  textTransform: "none",
  borderRadius: "6px",
  "& .MuiIcon-root": {
    fontSize: "14px !important",
  },
};

const tableHeaderCellSx = {
  backgroundColor: "#f8f9fa",
  color: "#8392ab",
  fontFamily: "Poppins, sans-serif",
  fontSize: "12px",
  fontWeight: 400,
  letterSpacing: 0,
  textTransform: "capitalize",
  borderBottom: "1px solid #e9ecef",
  py: 0.75,
  px: 0.35,
  whiteSpace: "normal",
  lineHeight: 1.15,
  verticalAlign: "middle",
};

const tableBodyCellSx = {
  color: "#334155",
  fontFamily: "Poppins, sans-serif",
  fontSize: "12px",
  fontWeight: 400,
  borderBottom: "1px solid #edf2f7",
  py: 0.4,
  px: 0.35,
  verticalAlign: "middle",
  lineHeight: 1.15,
  overflowWrap: "anywhere",
};

const detailHeaderCellSx = {
  ...tableHeaderCellSx,
  fontFamily: "Poppins, sans-serif",
  fontSize: "12px",
  fontWeight: 400,
  py: 0.55,
  px: 0.35,
  whiteSpace: "normal",
  lineHeight: 1.15,
};

const detailBodyCellSx = {
  ...tableBodyCellSx,
  fontFamily: "Poppins, sans-serif",
  fontSize: "12px",
  fontWeight: 400,
  py: 0.4,
  px: 0.35,
  lineHeight: 1.15,
  overflowWrap: "anywhere",
};

const detailSummaryTextSx = {
  fontFamily: "Poppins, sans-serif",
  fontSize: "12px",
  fontWeight: 400,
  lineHeight: 1.15,
};

const expandedDetailShellSx = {
  display: "grid",
  gridTemplateColumns: {
    xs: "1fr",
    lg: "minmax(320px, 0.48fr) minmax(0, 0.52fr)",
  },
  gap: 1,
  alignItems: "stretch",
};

const expandedPanelSx = {
  height: "100%",
  border: "1px solid #e2e8f0",
  borderRadius: "8px",
  backgroundColor: "#ffffff",
  boxShadow: "0 12px 28px rgba(15, 23, 42, 0.06)",
  overflow: "hidden",
};

const expandedPanelHeaderSx = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  flexWrap: "wrap",
  gap: 0.5,
  px: 1.25,
  py: 0.75,
  borderBottom: "1px solid #e8eef5",
  backgroundColor: "#f8fafc",
};

const expandedSummaryGridSx = {
  display: "grid",
  gridTemplateColumns: {
    xs: "1fr",
    sm: "repeat(2, minmax(0, 1fr))",
  },
  gap: 0.75,
  p: 1,
};

const expandedSummaryItemSx = {
  minWidth: 0,
  border: "1px solid #edf2f7",
  borderRadius: "6px",
  backgroundColor: "#fbfdff",
  px: 1,
  py: 0.75,
};

const expandedSummaryLabelSx = {
  display: "flex",
  alignItems: "center",
  gap: 0.5,
  mb: 0.35,
  color: "#64748b",
  "& .MuiIcon-root, & .material-icons": {
    fontSize: "14px !important",
    lineHeight: 1,
  },
};

const expandedTableContainerSx = {
  display: { xs: "none", lg: "block" },
  boxShadow: "none",
  overflowX: "hidden",
  borderRadius: 0,
  "& table": {
    borderCollapse: "separate",
    borderSpacing: 0,
    tableLayout: "fixed",
    width: "100%",
  },
  "& th, & td": {
    whiteSpace: "normal",
    overflowWrap: "anywhere",
    wordBreak: "break-word",
  },
  "& thead th": {
    backgroundColor: "#f1f5f9",
    color: "#475569",
    fontWeight: 600,
    fontSize: "10px",
    px: 0.35,
    borderBottom: "1px solid #dbe4ee",
  },
  "& tbody td": {
    fontSize: "9.5px",
    px: 0.35,
  },
  "& tbody tr:nth-of-type(even)": {
    backgroundColor: "#fbfdff",
  },
  "& tbody tr:hover": {
    backgroundColor: "#f8fbff",
  },
};

const expandedMobileDetailsSx = {
  display: { xs: "grid", lg: "none" },
  gridTemplateColumns: {
    xs: "1fr",
    sm: "repeat(2, minmax(0, 1fr))",
  },
  gap: 0.75,
  p: 1,
};

const expandedMobileDetailCardSx = {
  border: "1px solid #edf2f7",
  borderRadius: "6px",
  backgroundColor: "#fbfdff",
  overflow: "hidden",
};

const expandedMobileDetailGridSx = {
  display: "grid",
  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  gap: 0.5,
  p: 0.75,
};

const countryDetailGridSx = {
  display: "grid",
  gridTemplateColumns: {
    xs: "1fr",
    lg: "minmax(245px, 1.55fr) repeat(5, minmax(98px, 0.75fr)) minmax(145px, 0.95fr)",
  },
  gap: 0.5,
  alignItems: "start",
};

const wonCountryDetailGridSx = {
  display: "grid",
  gridTemplateColumns: {
    xs: "1fr",
    lg: "minmax(220px, 1.45fr) repeat(6, minmax(95px, 0.72fr)) minmax(145px, 0.95fr)",
  },
  gap: 0.5,
  alignItems: "start",
};

function BidList() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthReady, permissions = [], userPermission = [] } = useAuth();
  const { hasModulePermission, canViewField } = useModulePermissions(
    permissions,
    userPermission,
    "bids"
  );

  const canViewPage = hasModulePermission("bids.pages.list.view");
  const canOpenCreate =
    hasModulePermission("bids.actions.open_create") &&
    hasModulePermission("bids.actions.create");
  const canSubmitCreate = hasModulePermission("bids.actions.create");
  const canOpenEdit =
    hasModulePermission("bids.actions.open_edit") &&
    hasModulePermission("bids.actions.update");
  const canSubmitUpdate = hasModulePermission("bids.actions.update");
  const canOpenProjectTypeModal = hasModulePermission("bids.actions.open_project_type_modal");
  const canSubmitProjectType = hasModulePermission("bids.actions.submit_project_type");
  const canAddDetailRow = hasModulePermission("bids.actions.add_detail_row");
  const canRemoveDetailRow = hasModulePermission("bids.actions.remove_detail_row");
  const canOpenStatusMenu =
    canViewField("actions") && hasModulePermission("bids.actions.open_status_menu");
  const canHoldBid = hasModulePermission("bids.actions.hold");
  const canWonBid = hasModulePermission("bids.actions.won");
  const canCancelBid = hasModulePermission("bids.actions.cancel");
  const canMoveBidToInProgress = hasModulePermission("bids.actions.mark_in_progress");
  const canSearchBids = hasModulePermission("bids.actions.search");
  const canExportBids = hasModulePermission("bids.actions.export");
  const canExpandDetailsAction = hasModulePermission("bids.actions.expand_details");
  const canOpenWonModal = canWonBid;
  const canSubmitWon = canWonBid && hasModulePermission("bids.actions.submit_won");
  const canOpenRfqModal = hasModulePermission("bids.actions.open_rfq_modal");
  const canSubmitRfq = hasModulePermission("bids.actions.submit_rfq");
  const canUseBidFormOptions =
    canOpenCreate || canSubmitCreate || canOpenEdit || canSubmitUpdate || canOpenWonModal || canOpenRfqModal;

  const canViewAnyField = useCallback(
    (...fields) => fields.some((field) => canViewField(field)),
    [canViewField]
  );
  const canViewStatusTab = useCallback(
    (value) => hasModulePermission(statusTabPermissionKey(value)),
    [hasModulePermission]
  );

  const [bids, setBids] = useState([]);
  const [vendorFeasibilities, setVendorFeasibilities] = useState([]);
  const [bidCounts, setBidCounts] = useState({});
  const [listPagination, setListPagination] = useState({
    current_page: 1,
    last_page: 1,
    total: 0,
    from: 0,
    to: 0,
  });
  const [clients, setClients] = useState([]);
  const [projectManagerRoles, setProjectManagerRoles] = useState([]);
  const [projectTypes, setProjectTypes] = useState([]);
  const [bidProjectTypeOptions, setBidProjectTypeOptions] = useState([]);
  const [countries, setCountries] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [projectManagersLoading, setProjectManagersLoading] = useState(false);
  const [projectTypesLoading, setProjectTypesLoading] = useState(false);
  const [bidProjectTypeOptionsLoading, setBidProjectTypeOptionsLoading] = useState(false);
  const [countriesLoading, setCountriesLoading] = useState(false);
  const [vendorsLoading, setVendorsLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [bidDialogMode, setBidDialogMode] = useState("create");
  const [projectTypeDialogOpen, setProjectTypeDialogOpen] = useState(false);
  const [bidProjectTypeDialogOpen, setBidProjectTypeDialogOpen] = useState(false);
  const [newProjectType, setNewProjectType] = useState("");
  const [newBidProjectType, setNewBidProjectType] = useState("");
  const [masterSubmitLoading, setMasterSubmitLoading] = useState(false);
  const [statusSubmitLoading, setStatusSubmitLoading] = useState(false);
  const [actionsAnchor, setActionsAnchor] = useState(null);
  const [actionsBid, setActionsBid] = useState(null);
  const [clientSearch, setClientSearch] = useState("");
  const [listSearch, setListSearch] = useState("");
  const [listStartDate, setListStartDate] = useState("");
  const [listEndDate, setListEndDate] = useState("");
  const [listStudyTypeFilter, setListStudyTypeFilter] = useState([]);
  const [listProjectTypeFilter, setListProjectTypeFilter] = useState([]);
  const [listClientFilter, setListClientFilter] = useState([]);
  const [listPage, setListPage] = useState(1);
  const [expandedBidIds, setExpandedBidIds] = useState([]);
  const [activeStatusTab, setActiveStatusTab] = useState(() => statusTabFromSearch(location.search));
  const [bidUnreadTabs, setBidUnreadTabs] = useState({});
  const [form, setForm] = useState(createEmptyForm());
  const [wonDialogOpen, setWonDialogOpen] = useState(false);
  const [wonDialogBid, setWonDialogBid] = useState(null);
  const [wonDialogMode, setWonDialogMode] = useState("mark");
  const [wonForm, setWonForm] = useState(createWonForm());
  const [wonDetails, setWonDetails] = useState([]);
  const [wonVendors, setWonVendors] = useState([]);
  const [wonAttachments, setWonAttachments] = useState([]);
  const [vendorFeasibilityDialogOpen, setVendorFeasibilityDialogOpen] = useState(false);
  const [vendorFeasibilityDialogBid, setVendorFeasibilityDialogBid] = useState(null);
  const [vendorFeasibilityDialogTab, setVendorFeasibilityDialogTab] = useState("details");
  const [vendorFeasibilitySearch, setVendorFeasibilitySearch] = useState("");
  const [vendorFeasibilityStartDate, setVendorFeasibilityStartDate] = useState("");
  const [vendorFeasibilityEndDate, setVendorFeasibilityEndDate] = useState("");
  const [vendorFeasibilityVendorFilter, setVendorFeasibilityVendorFilter] = useState(null);
  const [selectedVendorFeasibilityKeys, setSelectedVendorFeasibilityKeys] = useState([]);
  const [vendorTargetCpiOverrides, setVendorTargetCpiOverrides] = useState({});
  const [confirmStatusDialog, setConfirmStatusDialog] = useState({
    open: false,
    bid: null,
    status: "",
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: "", color: "success" });

  const isBidDialogCreate = bidDialogMode === "create";
  const isBidDialogEdit = bidDialogMode === "edit";
  const isBidDialogWon = bidDialogMode === "won";
  const isBidDialogView = bidDialogMode === "view";
  const isBidDialogReadOnly = isBidDialogView;
  const canSubmitBidDialog = isBidDialogCreate
    ? canSubmitCreate
    : isBidDialogEdit
      ? canSubmitUpdate
      : isBidDialogWon && canSubmitWon;
  const canShowBidDialogField = useCallback(
    (...fields) => (isBidDialogCreate ? canOpenCreate : true) && canViewAnyField(...fields),
    [canOpenCreate, canViewAnyField, isBidDialogCreate]
  );

  const token = localStorage.getItem("authToken");

  const authHeaders = useMemo(
    () => ({
      Authorization: `Bearer ${token}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    }),
    [token]
  );

  const uploadHeaders = useMemo(
    () => ({
      Authorization: `Bearer ${token}`,
      Accept: "application/json",
    }),
    [token]
  );

  const visibleStatusTabs = useMemo(
    () => statusTabs.filter((tab) => canViewStatusTab(tab.value)),
    [canViewStatusTab]
  );

  const updateBidTabInUrl = useCallback(
    (tab) => {
      const params = new URLSearchParams(location.search);
      params.set("tab", tab);
      navigate(
        {
          pathname: location.pathname,
          search: `?${params.toString()}`,
        },
        { replace: true }
      );
    },
    [location.pathname, location.search, navigate]
  );

  const resetListFilters = useCallback(() => {
    setListSearch("");
    setListStartDate("");
    setListEndDate("");
    setListStudyTypeFilter([]);
    setListProjectTypeFilter([]);
    setListClientFilter([]);
  }, []);

  const switchStatusTab = useCallback(
    (value) => {
      if (value !== activeStatusTab) {
        resetListFilters();
      }

      setActiveStatusTab(value);
      setExpandedBidIds([]);
      updateBidTabInUrl(value);
    },
    [activeStatusTab, resetListFilters, updateBidTabInUrl]
  );

  useEffect(() => {
    if (!visibleStatusTabs.length) return;
    if (visibleStatusTabs.some((tab) => tab.value === activeStatusTab)) return;

    switchStatusTab(visibleStatusTabs[0].value);
  }, [activeStatusTab, switchStatusTab, visibleStatusTabs]);

  useEffect(() => {
    const tabFromUrl = statusTabFromSearch(location.search);
    if (tabFromUrl === activeStatusTab) return;

    resetListFilters();
    setActiveStatusTab(tabFromUrl);
    setExpandedBidIds([]);
  }, [activeStatusTab, location.search, resetListFilters]);

  const showMessage = useCallback((message, color = "success") => {
    setSnackbar({ open: true, message, color });
  }, []);

  const applyBidUnreadCounts = useCallback((counts) => {
    setBidUnreadTabs(counts?.bid_tabs || {});
  }, []);

  const tabFromNotification = useCallback((notification) => {
    const meta = notification?.meta || {};
    return normalizedStatus(meta.tab || meta.status || notification?.bid_tab || "in progress");
  }, []);

  const incrementBidUnreadFromNotification = useCallback(
    (notification) => {
      const meta = notification?.meta || {};
      const moduleName = meta.module || notification?.module;
      if (moduleName !== "bids") return;

      const tab = tabFromNotification(notification);
      setBidUnreadTabs((current) => ({
        ...current,
        [tab]: Number(current?.[tab] || 0) + 1,
      }));
    },
    [tabFromNotification]
  );

  const fetchBidNotificationCounts = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/notifications/counts?module=bids`, {
        headers: authHeaders,
      });
      const data = await readJsonSafely(response);
      if (response.ok) {
        applyBidUnreadCounts(data?.unread_counts || {});
      }
    } catch {
      applyBidUnreadCounts({});
    }
  }, [applyBidUnreadCounts, authHeaders]);

  const markBidTabNotificationsRead = useCallback(async (tabValue) => {
    try {
      const response = await fetch(`${API_URL}/notifications/mark-read`, {
        method: "POST",
        headers: authHeaders,
        body: JSON.stringify({ module: "bids", bid_tab: tabValue }),
      });
      const data = await readJsonSafely(response);
      if (response.ok) {
        applyBidUnreadCounts(data?.unread_counts || {});
        window.dispatchEvent(new CustomEvent("notification-counts-updated", {
          detail: {
            counts: data?.unread_counts || null,
            module: "bids",
            bid_tab: tabValue,
          },
        }));
      }
    } catch {
      // Count refresh is non-blocking for the bids table.
    }
  }, [applyBidUnreadCounts, authHeaders]);

  useEffect(() => {
    if (!canViewPage) return undefined;

    fetchBidNotificationCounts();
    const countsUpdatedHandler = (event) => {
      const payload = event.detail || {};
      const counts = payload.counts || payload;

      if (counts?.bid_tabs) {
        applyBidUnreadCounts(counts);
      }
    };
    const echoHandler = (event) => incrementBidUnreadFromNotification(event.detail);

    window.addEventListener("notification-counts-updated", countsUpdatedHandler);
    window.addEventListener("echo-event", echoHandler);

    return () => {
      window.removeEventListener("notification-counts-updated", countsUpdatedHandler);
      window.removeEventListener("echo-event", echoHandler);
    };
  }, [applyBidUnreadCounts, canViewPage, fetchBidNotificationCounts, incrementBidUnreadFromNotification]);

  const openActionsMenu = useCallback((event, bid) => {
    setActionsAnchor(event.currentTarget);
    setActionsBid(bid);
  }, []);

  const closeActionsMenu = useCallback(() => {
    if (statusSubmitLoading) return;
    setActionsAnchor(null);
    setActionsBid(null);
  }, [statusSubmitLoading]);

  const closeWonDialog = useCallback(() => {
    if (statusSubmitLoading) return;

    setWonDialogOpen(false);
    setWonDialogBid(null);
    setWonDialogMode("mark");
    setWonForm(createWonForm());
    setWonDetails([]);
    setWonVendors([]);
    setWonAttachments([]);
  }, [statusSubmitLoading]);

  const closeConfirmStatusDialog = useCallback(() => {
    if (statusSubmitLoading) return;

    setConfirmStatusDialog({ open: false, bid: null, status: "" });
  }, [statusSubmitLoading]);

  const resetVendorFeasibilityFilters = useCallback(() => {
    setVendorFeasibilitySearch("");
    setVendorFeasibilityStartDate("");
    setVendorFeasibilityEndDate("");
    setVendorFeasibilityVendorFilter(null);
    setSelectedVendorFeasibilityKeys([]);
  }, []);

  const closeVendorFeasibilityDialog = useCallback(() => {
    if (statusSubmitLoading) return;

    setVendorFeasibilityDialogOpen(false);
    setVendorFeasibilityDialogBid(null);
    setVendorFeasibilityDialogTab("details");
    setVendorTargetCpiOverrides({});
    resetVendorFeasibilityFilters();
  }, [resetVendorFeasibilityFilters, statusSubmitLoading]);

  const buildBidListParams = useCallback((page = 1, exportAll = false) => {
    const params = new URLSearchParams();
    params.set('tab', activeStatusTab);
    params.set('page', String(page));
    params.set('per_page', String(BID_LIST_PAGE_SIZE));
    if (exportAll) params.set('export', '1');
    if (listSearch.trim()) params.set('q', listSearch.trim());
    if (listStartDate) params.set('created_from', listStartDate);
    if (listEndDate) params.set('created_to', listEndDate);
    (Array.isArray(listStudyTypeFilter) ? listStudyTypeFilter : []).forEach((option) => {
      if (option?.value) params.append('study_type_ids[]', String(option.value));
    });
    (Array.isArray(listProjectTypeFilter) ? listProjectTypeFilter : []).forEach((option) => {
      if (option?.value) params.append('project_type_option_ids[]', String(option.value));
    });
    (Array.isArray(listClientFilter) ? listClientFilter : []).forEach((option) => {
      if (option?.value) params.append('client_ids[]', String(option.value));
    });
    return params;
  }, [activeStatusTab, listClientFilter, listEndDate, listProjectTypeFilter, listSearch, listStartDate, listStudyTypeFilter]);

  const fetchBids = useCallback(async (page = 1) => {
    if (!canViewPage) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const params = buildBidListParams(page);
      const response = await fetch(`${API_URL}/bids?${params.toString()}`, {
          headers: authHeaders,
        });
        const data = await readJsonSafely(response);

        if (!response.ok || data.success !== 1) {
          throw new Error(apiErrorMessage(data, "Failed to fetch bids"));
        }

      if (activeStatusTab === 'vendor feasibility') {
        const vendorPayload = data.vendor_feasibilities || [];
        const vendorRows = Array.isArray(vendorPayload.data) ? vendorPayload.data : vendorPayload;
        setBids([]);
        setVendorFeasibilities(Array.isArray(vendorRows) ? vendorRows : []);
        setListPagination({
          current_page: Number(vendorPayload.current_page || page),
          last_page: Math.max(Number(vendorPayload.last_page || 1), 1),
          total: Number(vendorPayload.total ?? vendorRows.length),
          from: Number(vendorPayload.from || (vendorRows.length ? 1 : 0)),
          to: Number(vendorPayload.to || vendorRows.length),
        });
        return;
      }

      const paginator = data.bids || {};
      const rows = Array.isArray(paginator.data) ? paginator.data : [];
      setBids(rows);
      setVendorFeasibilities([]);
      setListPagination({
        current_page: Number(paginator.current_page || page),
        last_page: Math.max(Number(paginator.last_page || 1), 1),
        total: Number(paginator.total ?? rows.length),
        from: Number(paginator.from || (rows.length ? 1 : 0)),
        to: Number(paginator.to || rows.length),
      });
    } catch (error) {
      showMessage(error.message || "Failed to fetch bids", "error");
    } finally {
      setLoading(false);
    }
  }, [authHeaders, buildBidListParams, canViewPage, showMessage]);

  const fetchBidCounts = useCallback(async () => {
    if (!canViewPage) return;

    try {
      const params = buildBidListParams(1);
      params.set('summary', '1');
      const response = await fetch(API_URL + '/bids?' + params.toString(), { headers: authHeaders });
      const data = await readJsonSafely(response);

      if (!response.ok || data.success !== 1) {
        throw new Error(apiErrorMessage(data, 'Failed to fetch bid counts'));
      }

      setBidCounts(data.counts || {});
    } catch (error) {
      showMessage(error.message || 'Failed to fetch bid counts', 'error');
    }
  }, [authHeaders, buildBidListParams, canViewPage, showMessage]);

  const fetchVendorFeasibilities = useCallback(async () => {
    if (!canViewPage) return;
    if (activeStatusTab !== 'vendor feasibility') return;

    try {
      const params = buildBidListParams(1);
      const response = await fetch(`${API_URL}/bids?${params.toString()}`, {
        headers: authHeaders,
      });
      const data = await readJsonSafely(response);

      if (!response.ok || data.success !== 1) {
        throw new Error(apiErrorMessage(data, "Failed to fetch vendor feasibilities"));
      }

      const payload = data.vendor_feasibilities || [];
      setVendorFeasibilities(Array.isArray(payload.data) ? payload.data : payload);
    } catch (error) {
      showMessage(error.message || "Failed to fetch vendor feasibilities", "error");
    }
  }, [activeStatusTab, authHeaders, buildBidListParams, canViewPage, showMessage]);

  useEffect(() => {
    if (!canViewPage) return undefined;

    const refreshVendorFeasibilities = (event) => {
      const notification = event.detail || {};
      const meta = notification.meta || {};
      const moduleName = meta.module || notification.module;
      const type = notification.type || meta.type;

      if (moduleName === "bids" && (type === "bid_rfq_vendor_submitted" || meta.invite_id)) {
        fetchVendorFeasibilities();
      }
    };

    window.addEventListener("echo-event", refreshVendorFeasibilities);

    return () => {
      window.removeEventListener("echo-event", refreshVendorFeasibilities);
    };
  }, [canViewPage, fetchVendorFeasibilities]);

  const fetchClients = useCallback(
    async (search = "") => {
      if (!canViewPage || !canViewAnyField("client_name", "client_code")) return;

      try {
        setClientsLoading(true);
        const params = new URLSearchParams({ limit: "500" });
        if (search) params.set("q", search);

        const response = await fetch(`${API_URL}/bids/clients?${params.toString()}`, {
          headers: authHeaders,
        });
        const data = await readJsonSafely(response);

        if (!response.ok || data.success !== 1) {
          throw new Error(apiErrorMessage(data, "Failed to fetch clients"));
        }

        setClients((data.clients || []).map(toClientOption));
      } catch (error) {
        showMessage(error.message || "Failed to fetch clients", "error");
      } finally {
        setClientsLoading(false);
      }
    },
    [authHeaders, canViewAnyField, canViewPage, showMessage]
  );

  const fetchProjectManagers = useCallback(async () => {
    if (
      !canUseBidFormOptions ||
      !canViewField("project_manager_role")
    ) {
      return;
    }

    try {
      setProjectManagersLoading(true);
      const response = await fetch(`${API_URL}/bids/project-managers`, {
        headers: authHeaders,
      });
      const data = await readJsonSafely(response);

      if (!response.ok || data.success !== 1) {
        throw new Error(apiErrorMessage(data, "Failed to fetch Project Management Team"));
      }

      const roles = (data.roles || data.managers || []).map((role) => ({
        ...toOption(role),
        users: (role.users || []).map(toOption),
      }));

      setProjectManagerRoles(roles);
    } catch (error) {
      showMessage(error.message || "Failed to fetch Project Management Team", "error");
    } finally {
      setProjectManagersLoading(false);
    }
  }, [authHeaders, canUseBidFormOptions, canViewField, showMessage]);

  const fetchProjectTypes = useCallback(async () => {
    if (!canViewPage || !canViewField("project_type")) return;

    try {
      setProjectTypesLoading(true);
      const response = await fetch(`${API_URL}/bids/project-types`, {
        headers: authHeaders,
      });
      const data = await readJsonSafely(response);

      if (!response.ok || data.success !== 1) {
        throw new Error(apiErrorMessage(data, "Failed to fetch project types"));
      }

      setProjectTypes((data.project_types || []).map(toOption));
    } catch (error) {
      showMessage(error.message || "Failed to fetch project types", "error");
    } finally {
      setProjectTypesLoading(false);
    }
  }, [authHeaders, canViewField, canViewPage, showMessage]);

  const fetchBidProjectTypeOptions = useCallback(async () => {
    if (!canViewPage || !canViewField("project_type")) return;

    try {
      setBidProjectTypeOptionsLoading(true);
      const response = await fetch(`${API_URL}/bids/project-type-options`, {
        headers: authHeaders,
      });
      const data = await readJsonSafely(response);

      if (!response.ok || data.success !== 1) {
        throw new Error(apiErrorMessage(data, "Failed to fetch project type options"));
      }

      setBidProjectTypeOptions((data.project_type_options || []).map(toOption));
    } catch (error) {
      showMessage(error.message || "Failed to fetch project type options", "error");
    } finally {
      setBidProjectTypeOptionsLoading(false);
    }
  }, [authHeaders, canViewField, canViewPage, showMessage]);

  const fetchCountries = useCallback(async () => {
    if (!canUseBidFormOptions || !canViewField("country")) return;

    try {
      setCountriesLoading(true);
      const response = await fetch(`${API_URL}/bids/countries`, {
        headers: authHeaders,
      });
      const data = await readJsonSafely(response);

      if (!response.ok || data.success !== 1) {
        throw new Error(apiErrorMessage(data, "Failed to fetch countries"));
      }

      setCountries((data.countries || []).map(toOption));
    } catch (error) {
      showMessage(error.message || "Failed to fetch countries", "error");
    } finally {
      setCountriesLoading(false);
    }
  }, [authHeaders, canUseBidFormOptions, canViewField, showMessage]);

  const fetchVendors = useCallback(async (bidForVendors = null, excludeWon = null, excludeSentRfq = null) => {
    const selectedBid = bidForVendors || wonDialogBid;
    if (!selectedBid?.id || !canViewField("vendors")) return;

    try {
      setVendorsLoading(true);
      const shouldExcludeWon = excludeWon ?? false;
      const shouldExcludeSentRfq = excludeSentRfq ?? wonDialogMode === "rfq";
      const params = new URLSearchParams({
        limit: "500",
        exclude_won: shouldExcludeWon ? "1" : "0",
        exclude_sent_rfq: shouldExcludeSentRfq ? "1" : "0",
      });
      const response = await fetch(`${API_URL}/bids/${selectedBid.id}/available-vendors?${params.toString()}`, {
        headers: authHeaders,
      });
      const data = await readJsonSafely(response);

      if (!response.ok || data.success !== 1) {
        throw new Error(apiErrorMessage(data, "Failed to fetch vendors"));
      }

      const vendorOptions = (data.vendors || []).map(vendorToOption);
      setVendors(vendorOptions);

      if (shouldExcludeSentRfq) {
        const availableVendorIds = new Set(vendorOptions.map((vendor) => String(vendor.value)));
        setWonVendors((current) =>
          current.filter((vendor) => availableVendorIds.has(String(vendor.value)))
        );
      }
    } catch (error) {
      showMessage(error.message || "Failed to fetch vendors", "error");
    } finally {
      setVendorsLoading(false);
    }
  }, [authHeaders, canViewField, showMessage, wonDialogBid, wonDialogMode]);

  useEffect(() => {
    if (!isAuthReady) return;
    fetchBidCounts();
    fetchBids(1);
  }, [fetchBidCounts, fetchBids, isAuthReady]);

  useEffect(() => {
    if (!isAuthReady || !canViewPage) return;

    fetchClients();
    fetchProjectTypes();
    fetchBidProjectTypeOptions();
  }, [
    canViewPage,
    fetchBidProjectTypeOptions,
    fetchClients,
    fetchProjectTypes,
    isAuthReady,
  ]);

  useEffect(() => {
    if (!isAuthReady || listPage <= 1) return;
    fetchBids(listPage);
  }, [fetchBids, isAuthReady, listPage]);

  useEffect(() => {
    if (!addDialogOpen) return undefined;

    const timeout = setTimeout(() => {
      fetchClients(clientSearch);
    }, 300);

    return () => clearTimeout(timeout);
  }, [addDialogOpen, clientSearch, fetchClients]);

  useEffect(() => {
    if (!addDialogOpen) return;

    fetchProjectManagers();
    fetchProjectTypes();
    fetchBidProjectTypeOptions();
    fetchCountries();
  }, [addDialogOpen, fetchBidProjectTypeOptions, fetchCountries, fetchProjectManagers, fetchProjectTypes]);

  useEffect(() => {
    if (
      wonDialogMode !== "mark" ||
      !wonDialogOpen ||
      !wonDialogBid ||
      wonVendors.length ||
      !vendors.length
    ) return;

    const existingVendorIds = Array.isArray(wonDialogBid.won_vendor_ids)
      ? wonDialogBid.won_vendor_ids.map(String)
      : [];
    if (!existingVendorIds.length) return;

    setWonVendors(vendors.filter((vendor) => existingVendorIds.includes(String(vendor.value))));
  }, [vendors, wonDialogBid, wonDialogMode, wonDialogOpen, wonVendors.length]);

  const openAddDialog = () => {
    if (!canOpenCreate) {
      showMessage("You do not have permission to add bids", "error");
      return;
    }

    setForm(createEmptyForm());
    setBidDialogMode("create");
    setClientSearch("");
    setAddDialogOpen(true);
  };

  const closeAddDialog = () => {
    if (submitLoading) return;
    setAddDialogOpen(false);
    setBidDialogMode("create");
    setForm(createEmptyForm());
    setWonDialogBid(null);
    setWonDialogMode("mark");
  };

  const formFromBid = useCallback((bid) => {
    const details = Array.isArray(bid?.details) && bid.details.length
      ? bid.details.map((detail) => ({
        id: detail.id,
        country: countryOptionFromDetail(detail),
        country_id: detail.country_id || detail.country?.id || "",
        targeting: detail.targeting || "",
        loi: detail.loi ?? "",
        ir: detail.ir ?? "",
        required_n: detail.required_n ?? "",
        bid_cpi: detail.bid_cpi ?? "",
        won_cpi: valueOrFallback(detail.won_cpi, detail.bid_cpi ?? ""),
        feasibility: detail.feasibility || "",
        required_field_time_days: integerInput(detail.required_field_time_days, 7),
      }))
      : [{ ...emptyDetail }];

    return {
      id: bid?.id || null,
      status: bid?.status || "",
      project_title: bid?.project_title || "",
      project_manager_role: projectManagerRoleOptionFromBid(bid),
      project_manager: projectManagerOptionFromBid(bid),
      project_type: projectTypeOptionFromBid(bid),
      project_type_option: bidProjectTypeOptionFromBid(bid),
      client: clientOptionFromBid(bid),
      project_launch_date: toDateTimeLocalInput(bid?.project_launch_date),
      attachments: [],
      existing_attachments: normalizeAttachments(bid?.attachment_docs),
      comments: bid?.comments || "",
      details,
    };
  }, []);

  const openBidDialogForBid = useCallback((bid) => {
    if (!bid?.id) return;

    const currentStatus = normalizedStatus(bid.status);
    const canEditCurrentBid = editableBidStatuses.includes(currentStatus) && canOpenEdit;

    setForm(formFromBid(bid));
    setBidDialogMode(canEditCurrentBid ? "edit" : "view");
    setClientSearch("");
    setAddDialogOpen(true);
    setActionsAnchor(null);
    setActionsBid(null);
  }, [canOpenEdit, formFromBid]);

  const updateForm = (field, value) => {
    setForm((current) => ({
      ...current,
      [field]: value,
      ...(field === "project_manager_role" ? { project_manager: null } : {}),
    }));
  };

  const updateDetail = (index, field, value) => {
    const nextValue = (() => {
      if (field === "bid_cpi" || field === "won_cpi") return decimalInput(value, 3, 2);
      if (field === "required_n") return digitsOnly(value, 7);
      if (field === "feasibility") return digitsOnly(value, 7);
      if (field === "required_field_time_days") return integerInput(value, 7);

      return value;
    })();

    setForm((current) => ({
      ...current,
      details: current.details.map((detail, detailIndex) =>
        detailIndex === index ? { ...detail, [field]: nextValue } : detail
      ),
    }));
  };

  const isAllowedBidAttachment = (file) => {
    const extension = String(file?.name || "").split(".").pop().toLowerCase();

    return file?.type?.startsWith("image/") || BID_ATTACHMENT_EXTENSIONS.includes(extension);
  };

  const validateBidAttachmentFiles = (files, existingCount = 0) => {
    if (existingCount + files.length > BID_MAX_ATTACHMENTS) {
      showMessage("Maximum 5 attachments allowed.", "error");
      return false;
    }

    const oversizedFile = files.find((file) => file.size > BID_MAX_ATTACHMENT_SIZE_BYTES);
    if (oversizedFile) {
      showMessage(`${oversizedFile.name} 30 MB se bada hai.`, "error");
      return false;
    }

    const invalidFile = files.find((file) => !isAllowedBidAttachment(file));
    if (invalidFile) {
      showMessage(`${invalidFile.name} allowed file type nahi hai.`, "error");
      return false;
    }

    return true;
  };

  const updateBidAttachments = (files) => {
    if (!validateBidAttachmentFiles(files, form.existing_attachments.length)) return;

    updateForm("attachments", files);
  };

  const addDetailRow = () => {
    if (!canAddDetailRow) {
      showMessage("You do not have permission to add bid details", "error");
      return;
    }

    setForm((current) => ({
      ...current,
      details: [
        ...current.details,
        {
          ...(current.details[0] || emptyDetail),
          country: current.details[0]?.country ? { ...current.details[0].country } : null,
        },
      ],
    }));
  };

  const removeDetailRow = (index) => {
    if (!canRemoveDetailRow) {
      showMessage("You do not have permission to remove bid details", "error");
      return;
    }

    setForm((current) => ({
      ...current,
      details: current.details.length > 1 ? current.details.filter((_, i) => i !== index) : current.details,
    }));
  };

  const createProjectType = async () => {
    if (!canSubmitProjectType) {
      showMessage("You do not have permission to create study types", "error");
      return;
    }

    const name = newProjectType.trim();
    if (!name) {
      showMessage("Study type name is required", "error");
      return;
    }

    try {
      setMasterSubmitLoading(true);
      const response = await fetch(`${API_URL}/bids/project-types`, {
        method: "POST",
        headers: authHeaders,
        body: JSON.stringify({ name }),
      });
      const data = await readJsonSafely(response);

      if (!response.ok || data.success !== 1) {
        throw new Error(apiErrorMessage(data, "Failed to create study type"));
      }

      const option = toOption(data.project_type);
      setProjectTypes((current) => [...current.filter((item) => item.value !== option.value), option]);
      updateForm("project_type", option);
      setProjectTypeDialogOpen(false);
      setNewProjectType("");
      showMessage(apiSuccessMessage(data, "Study type created successfully"), "success");
    } catch (error) {
      showMessage(error.message || "Failed to create study type", "error");
    } finally {
      setMasterSubmitLoading(false);
    }
  };

  const createBidProjectTypeOption = async () => {
    if (!canSubmitProjectType) {
      showMessage("You do not have permission to create project types", "error");
      return;
    }

    const name = newBidProjectType.trim();
    if (!name) {
      showMessage("Project type name is required", "error");
      return;
    }

    try {
      setMasterSubmitLoading(true);
      const response = await fetch(`${API_URL}/bids/project-type-options`, {
        method: "POST",
        headers: authHeaders,
        body: JSON.stringify({ name }),
      });
      const data = await readJsonSafely(response);

      if (!response.ok || data.success !== 1) {
        throw new Error(apiErrorMessage(data, "Failed to create project type"));
      }

      const option = toOption(data.project_type_option);
      setBidProjectTypeOptions((current) => [
        ...current.filter((item) => item.value !== option.value),
        option,
      ]);
      updateForm("project_type_option", option);
      setBidProjectTypeDialogOpen(false);
      setNewBidProjectType("");
      showMessage(apiSuccessMessage(data, "Project type created successfully"), "success");
    } catch (error) {
      showMessage(error.message || "Failed to create project type", "error");
    } finally {
      setMasterSubmitLoading(false);
    }
  };

  const validateForm = () => {
    const missing = [];

    if (!form.project_title.trim()) missing.push("Project Title");
    if (!form.project_manager_role?.value) missing.push("Project Management Team");
    if (!form.client?.value) missing.push("Client");
    if (!form.project_type?.value) missing.push("Study Type");
    if (!form.project_type_option?.value) missing.push("Project Type");
    if (!form.details.length || form.details.some((detail) => !detail.country?.value)) {
      missing.push("Country");
    }

    if (missing.length) {
      showMessage(`${missing.join(", ")} required hai.`, "error");
      return false;
    }

    if (!validateBidAttachmentFiles(form.attachments, form.existing_attachments.length)) return false;

    return true;
  };

  const buildBidFormData = () => {
    const bidFormData = new FormData();

    bidFormData.append("project_title", form.project_title || "");
    bidFormData.append("project_manager_role_id", form.project_manager_role?.value || "");
    bidFormData.append("client_id", form.client?.value || "");
    bidFormData.append("project_type_id", form.project_type?.value || "");
    bidFormData.append("project_type_option_id", form.project_type_option?.value || "");
    bidFormData.append("project_launch_date", form.project_launch_date || "");
    bidFormData.append("comments", form.comments || "");
    form.details.forEach((detail, index) => {
      const detailPayload = {
        id: detail.id || "",
        country_id: detail.country?.value || detail.country_id || "",
        targeting: detail.targeting || "",
        loi: detail.loi ?? "",
        ir: detail.ir ?? "",
        required_n: detail.required_n ?? "",
        bid_cpi: trimTrailingDecimal(detail.bid_cpi),
        feasibility: detail.feasibility ?? "",
        required_field_time_days: integerInput(detail.required_field_time_days, 7),
      };
      if (isBidDialogWon && canShowBidDialogField("won_cpi")) {
        detailPayload.won_cpi = trimTrailingDecimal(detail.won_cpi);
      }

      Object.entries(detailPayload).forEach(([key, value]) => {
        bidFormData.append(`details[${index}][${key}]`, value);
      });
    });
    form.attachments.forEach((file) => {
      bidFormData.append("attachment_docs[]", file);
    });

    return bidFormData;
  };

  const handleSubmit = async () => {
    if (isBidDialogReadOnly) return;

    if (isBidDialogCreate && !canSubmitCreate) {
      showMessage("You do not have permission to create bids", "error");
      return;
    }

    if (isBidDialogEdit && !canSubmitUpdate) {
      showMessage("You do not have permission to update bids", "error");
      return;
    }

    if (isBidDialogWon && !canSubmitWon) {
      showMessage("You do not have permission to mark bids won", "error");
      return;
    }

    if ((isBidDialogEdit || isBidDialogWon) && !form.id) {
      showMessage("Bid not found for update", "error");
      return;
    }

    if (!validateForm()) return;

    if (isBidDialogWon) {
      setSubmitLoading(true);
      const updated = await updateBidStatus(wonDialogBid || form, "won", buildBidFormData());
      if (updated) {
        setAddDialogOpen(false);
        setBidDialogMode("create");
        setForm(createEmptyForm());
        setWonDialogBid(null);
        setWonDialogMode("mark");
      }
      setSubmitLoading(false);
      return;
    }

    try {
      setSubmitLoading(true);
      const isUpdate = isBidDialogEdit;
      const response = await fetch(isUpdate ? `${API_URL}/bids/${form.id}` : `${API_URL}/bids`, {
        method: "POST",
        headers: uploadHeaders,
        body: buildBidFormData(),
      });
      const data = await readJsonSafely(response);

      if (!response.ok || data.success !== 1) {
        throw new Error(apiErrorMessage(data, isUpdate ? "Failed to update bid" : "Failed to create bid"));
      }

      showMessage(
        apiSuccessMessage(data, isUpdate ? "Bid updated successfully" : "Bid created successfully"),
        "success"
      );
      setAddDialogOpen(false);
      setBidDialogMode("create");
      setForm(createEmptyForm());
      if (isUpdate) {
        const updatedBid = data.bid;
        setBids((current) =>
          current.map((item) => (String(item.id) === String(form.id) ? updatedBid || item : item))
        );
      } else {
        fetchBids();
      }
    } catch (error) {
      showMessage(error.message || (isBidDialogEdit ? "Failed to update bid" : "Failed to create bid"), "error");
    } finally {
      setSubmitLoading(false);
    }
  };

  const updateBidStatus = async (bid, status, extraPayload = {}) => {
    if (!bid?.id) return;

    const permissionAllowed = status === "hold"
      ? canHoldBid
      : status === "won"
        ? canSubmitWon
        : status === "canceled"
          ? canCancelBid
          : canMoveBidToInProgress;
    if (!permissionAllowed) {
      showMessage("You do not have permission to update bid status", "error");
      return;
    }

    try {
      setStatusSubmitLoading(true);
      const isMultipart = extraPayload instanceof FormData;
      if (isMultipart) {
        extraPayload.append("status", status);
      }

      const response = await fetch(`${API_URL}/bids/${bid.id}/status`, {
        method: isMultipart ? "POST" : "PATCH",
        headers: isMultipart ? uploadHeaders : authHeaders,
        body: isMultipart ? extraPayload : JSON.stringify({ status, ...extraPayload }),
      });
      const data = await readJsonSafely(response);

      if (!response.ok || data.success !== 1) {
        throw new Error(apiErrorMessage(data, "Failed to update bid status"));
      }

      const updatedBid = data.bid;
      setBids((current) =>
        current.map((item) => (String(item.id) === String(bid.id) ? updatedBid || { ...item, status } : item))
      );
      if (status === "won" && (bid.row_key || bid.vendor_feasibility_invite_id)) {
        setVendorFeasibilities((current) =>
          current.filter(
            (item) =>
              String(item.id) !== String(bid.id) &&
              String(item.row_key || "") !== String(bid.row_key || "") &&
              String(item.vendor_feasibility_invite_id || "") !==
              String(bid.vendor_feasibility_invite_id || "")
          )
        );
      }
      if (["in progress", "hold", "won", "canceled"].includes(status)) {
        switchStatusTab(status);
      }
      showMessage(apiSuccessMessage(data, "Bid status updated successfully"), "success");
      setActionsAnchor(null);
      setActionsBid(null);
      return true;
    } catch (error) {
      showMessage(error.message || "Failed to update bid status", "error");
      return false;
    } finally {
      setStatusSubmitLoading(false);
    }
  };

  const openWonDialogForBid = (bid, mode = "mark") => {
    if (!bid?.id) return;
    const modeAllowed = mode === "rfq" ? canOpenRfqModal : canOpenWonModal;

    if (!modeAllowed) {
      showMessage("You do not have permission for this bid action", "error");
      return;
    }

    if (mode === "mark") {
      setForm(formFromBid(bid));
      setBidDialogMode("won");
      setWonDialogBid(bid);
      setWonDialogMode("mark");
      setClientSearch("");
      setAddDialogOpen(true);
      setActionsAnchor(null);
      setActionsBid(null);
      fetchClients();
      fetchProjectManagers();
      fetchProjectTypes();
      fetchBidProjectTypeOptions();
      fetchCountries();
      return;
    }

    const existingVendorIds = Array.isArray(bid?.won_vendor_ids) ? bid.won_vendor_ids.map(String) : [];
    const lockedVendor = bid?.vendor_feasibility_vendor ? vendorToOption(bid.vendor_feasibility_vendor) : null;
    const submittedVendorOptions = Array.isArray(bid?.vendor_feasibility_vendor_options)
      ? Array.from(
          new Map(
            bid.vendor_feasibility_vendor_options
              .map(vendorToOption)
              .filter((vendor) => vendor.value)
              .map((vendor) => [String(vendor.value), vendor])
          ).values()
        )
      : [];
    const initialWonVendors = lockedVendor
      ? [lockedVendor]
      : submittedVendorOptions.length || mode === "rfq"
        ? []
        : vendors.filter((vendor) => existingVendorIds.includes(String(vendor.value)));

    setWonDialogMode(mode);
    setWonDialogBid(bid);
    setWonForm({
      project_title: bid?.project_title || "",
      project_manager_role: projectManagerRoleOptionFromBid(bid),
      project_manager: projectManagerOptionFromBid(bid),
      project_type: projectTypeOptionFromBid(bid),
      project_type_option: bidProjectTypeOptionFromBid(bid),
      client: clientOptionFromBid(bid),
      project_launch_date: toDateTimeLocalInput(bid?.project_launch_date),
      comments: bid?.comments || "",
    });
    setWonDetails(
      bidDetails(bid).map((detail) => ({
        id: detail.id,
        country: countryOptionFromDetail(detail),
        country_id: detail.country_id || detail.country?.id || "",
        loi: detail.loi,
        ir: detail.ir,
        bid_cpi: detail.bid_cpi,
        won_cpi: valueOrFallback(detail.won_cpi, detail.bid_cpi ?? ""),
        required_n: detail.required_n,
        feasibility: detail.feasibility,
        required_field_time_days: integerInput(detail.required_field_time_days, 7),
        targeting: detail.targeting,
      }))
    );
    setWonVendors(initialWonVendors);
    if (lockedVendor) {
      setVendors([lockedVendor]);
    } else if (submittedVendorOptions.length) {
      setVendors(submittedVendorOptions);
    }
    setWonAttachments([]);
    setWonDialogOpen(true);
    setActionsAnchor(null);
    fetchClients();
    fetchProjectManagers();
    fetchProjectTypes();
    fetchBidProjectTypeOptions();
    fetchCountries();
    if (!lockedVendor && !submittedVendorOptions.length) {
      fetchVendors(bid, false, mode === "rfq");
    }
  };

  const handleRfqToVendors = (bid) => {
    if (!canOpenRfqModal) {
      showMessage("You do not have permission to send RFQ to vendors", "error");
      return;
    }

    openWonDialogForBid(bid, "rfq");
  };

  const updateWonForm = (field, value) => {
    setWonForm((current) => ({
      ...current,
      [field]: value,
      ...(field === "project_manager_role" ? { project_manager: null } : {}),
    }));
  };

  const updateWonDetail = (index, field, value) => {
    const nextValue = (() => {
      if (field === "bid_cpi" || field === "won_cpi") return decimalInput(value, 3, 2);
      if (field === "required_n") return digitsOnly(value, 7);
      if (field === "feasibility") return digitsOnly(value, 7);
      if (field === "required_field_time_days") return integerInput(value, 7);

      return value;
    })();

    setWonDetails((current) =>
      current.map((detail, detailIndex) =>
        detailIndex === index ? { ...detail, [field]: nextValue } : detail
      )
    );
  };

  const statusActionsForBid = useCallback(
    (bid) => {
      if (bid?.vendor_feasibility_invite_id) {
        return [
          canOpenWonModal && { status: "won" },
        ].filter(Boolean);
      }

      const currentStatus = normalizedStatus(bid?.status);

      if (currentStatus === "hold") {
        return [
          canMoveBidToInProgress && { status: "in progress" },
          canCancelBid && { status: "canceled" },
        ].filter(Boolean);
      }

      if (currentStatus === "canceled") {
        return [
          canMoveBidToInProgress && { status: "in progress" },
          canHoldBid && { status: "hold" },
        ].filter(Boolean);
      }

      if (currentStatus === "won") {
        return [];
      }

      return [
        canHoldBid && { status: "hold" },
        canOpenWonModal && { status: "won" },
        canCancelBid && { status: "canceled" },
      ].filter(Boolean);
    },
    [canCancelBid, canHoldBid, canMoveBidToInProgress, canOpenWonModal]
  );

  const requestStatusChange = async (bid, status) => {
    if (!bid) return;

    if (status === "won") {
      openWonDialogForBid(bid, "mark");
      return;
    }

    if (["hold", "canceled"].includes(status)) {
      setConfirmStatusDialog({ open: true, bid, status });
      setActionsAnchor(null);
      return;
    }

    await updateBidStatus(bid, status);
  };

  const confirmStatusChange = async () => {
    const { bid, status } = confirmStatusDialog;
    if (!bid || !status) return;

    const updated = await updateBidStatus(bid, status);
    if (updated) {
      setConfirmStatusDialog({ open: false, bid: null, status: "" });
    }
  };

  const submitWonBid = async () => {
    if (!wonDialogBid) return;

    if (!isRfqMode) return;

    if (!canViewField("vendors")) {
      showMessage("You do not have permission to select vendors", "error");
      return;
    }

    if (!canSubmitRfq) {
      showMessage("You do not have permission to send RFQ to vendors", "error");
      return;
    }

    const buildBidVendorPayload = () => {
      const payload = new FormData();
      if (canViewField("project_title")) payload.append("project_title", wonForm.project_title ?? "");
      if (canViewField("project_manager_role")) {
        payload.append("project_manager_role_id", wonForm.project_manager_role?.value ?? "");
      }
      if (canViewField("project_type")) {
        payload.append("project_type_id", wonForm.project_type?.value ?? "");
        payload.append("project_type_option_id", wonForm.project_type_option?.value ?? "");
      }
      if (canViewAnyField("client_name", "client_code")) {
        payload.append("client_id", wonForm.client?.value ?? "");
      }
      if (canViewField("project_launch_date")) {
        payload.append("project_launch_date", wonForm.project_launch_date ?? "");
      }
      if (canViewField("comments")) payload.append("comments", wonForm.comments ?? "");

      wonVendors.forEach((vendor, index) => {
        payload.append(`vendor_ids[${index}]`, vendor.value);
      });

      wonDetails.forEach((detail, index) => {
        payload.append(`details[${index}][id]`, detail.id ?? "");
        if (canViewField("country")) {
          payload.append(`details[${index}][country_id]`, detail.country?.value || detail.country_id || "");
        }
        if (canViewField("targeting")) payload.append(`details[${index}][targeting]`, detail.targeting ?? "");
        if (canViewField("loi")) payload.append(`details[${index}][loi]`, detail.loi ?? "");
        if (canViewField("ir")) payload.append(`details[${index}][ir]`, detail.ir ?? "");
        if (canViewField("required_n")) payload.append(`details[${index}][required_n]`, detail.required_n ?? "");
        if (canViewField("bid_cpi")) payload.append(`details[${index}][bid_cpi]`, trimTrailingDecimal(detail.bid_cpi));
        if (canViewField("won_cpi")) payload.append(`details[${index}][won_cpi]`, trimTrailingDecimal(detail.won_cpi));
        if (canViewField("feasibility")) payload.append(`details[${index}][feasibility]`, detail.feasibility ?? "");
        if (canViewField("required_field_time_days")) {
          payload.append(
            `details[${index}][required_field_time_days]`,
            integerInput(detail.required_field_time_days, 7)
          );
        }
      });

      wonAttachments.forEach((file) => {
        if (canViewField("attachment_docs")) payload.append("attachment_docs[]", file);
      });

      return payload;
    };

    if (!wonVendors.length) {
      showMessage("Please select at least one vendor.", "error");
      return;
    }

    try {
      setStatusSubmitLoading(true);
      const response = await fetch(`${API_URL}/bids/${wonDialogBid.id}/rfq-vendors`, {
        method: "POST",
        headers: uploadHeaders,
        body: buildBidVendorPayload(),
      });
      const data = await readJsonSafely(response);

      if (!response.ok || data.success !== 1) {
        throw new Error(apiErrorMessage(data, "Failed to send RFQ to vendors"));
      }

      const updatedBid = data.bid;
      setBids((current) =>
        current.map((item) => (String(item.id) === String(wonDialogBid.id) ? updatedBid || item : item))
      );
      showMessage(apiSuccessMessage(data, "RFQ sent to vendors successfully"), "success");
      closeWonDialog();
    } catch (error) {
      showMessage(error.message || "Failed to send RFQ to vendors", "error");
    } finally {
      setStatusSubmitLoading(false);
    }
  };

  const tableColumns = useMemo(() => {
    const list = [];

    if (canViewField("quest_code")) list.push({ key: "quest_code", label: "Project Code", width: "12%" });
    if (canViewField("project_title")) list.push({ key: "project_title", label: "Project Title", width: "16%" });
    if (canViewField("project_manager_role")) {
      list.push({ key: "project_manager", label: "Project Management Team", width: "14%" });
    }
    if (canViewField("project_type")) {
      list.push({ key: "project_type", label: "Study Type", width: "10%" });
      list.push({ key: "project_type_option", label: "Project Type", width: "10%" });
    }
    if (canViewAnyField("client_name", "client_code")) {
      list.push({ key: "client", label: "Client Name", width: "19%" });
    }
    if (activeStatusTab === "vendor feasibility" && canViewAnyField("vendors", "listing_vendor")) {
      list.push({ key: "listing_vendor", label: "Vendors", width: "16%" });
    }
    if (canViewField("project_launch_date")) {
      list.push({ key: "project_launch_date", label: "Project Launch Date", width: "14%" });
    }
    if (canViewField("created_at")) {
      list.push({ key: "created_date", label: "Created Date", width: "10%" });
    }

    if (activeStatusTab !== "vendor feasibility" && canViewField("actions")) {
      list.push({ key: "actions", label: "Action", align: "center", width: "5%" });
    }
    return list;
  }, [activeStatusTab, canViewAnyField, canViewField]);

  const detailColumns = useMemo(() => {
    const list = [];

    if (canViewField("country")) {
      list.push({ key: "country", label: "Country/Geo", width: "12%" });
    }
    if (canViewField("loi")) list.push({ key: "loi", label: "LOI(mins)", width: "7%" });
    if (canViewField("ir")) list.push({ key: "ir", label: "IR(%)", width: "7%" });
    if (canViewField("bid_cpi")) list.push({ key: "bid_cpi", label: "Bid CPI ($)", width: "10%" });
    if (canViewField("won_cpi")) list.push({ key: "won_cpi", label: "Won CPI ($)", width: "10%" });
    if (canViewField("required_n")) list.push({ key: "required_n", label: "Required N", width: "8%" });
    if (canViewField("feasibility")) list.push({ key: "feasibility", label: "Feasibility", width: "10%" });
    if (canViewField("required_field_time_days")) {
      list.push({ key: "required_field_time_days", label: "Required Field Time (Days)", width: "13%" });
    }
    if (canViewField("targeting")) list.push({ key: "targeting", label: "Targeting", width: "23%" });

    return list;
  }, [canViewAnyField, canViewField]);

  const vendorFeasibilitySubmittedColumns = useMemo(() => {
    const list = [];

    if (canViewField("country")) {
      list.push({ key: "country", label: "Country/Geo", width: "12%" });
    }
    if (canViewField("targeting")) list.push({ key: "targeting", label: "Target", width: "23%" });
    if (canViewField("loi")) list.push({ key: "loi", label: "LOI(mins)", width: "7%" });
    if (canViewField("ir")) list.push({ key: "ir", label: "IR(%)", width: "7%" });
    if (canViewField("required_n")) list.push({ key: "required_n", label: "Req. N", width: "8%" });
    if (canViewAnyField("bid_cpi", "won_cpi")) {
      list.push({ key: "target_cpi", label: "Target CPI ($)", width: "10%" });
    }
    if (canViewField("feasibility")) list.push({ key: "feasibility", label: "Feasibility", width: "10%" });
    if (canViewField("required_field_time_days")) {
      list.push({ key: "required_field_time_days", label: "Required Field Time (Days)", width: "13%" });
    }
    if (canOpenWonModal && canSubmitWon) {
      list.push({ key: "country_won_action", label: "Won", width: "8%" });
    }

    return list;
  }, [canOpenWonModal, canSubmitWon, canViewAnyField, canViewField]);

  const detailSummaryFieldsForBid = useCallback((bid) => {
    const list = [];

    if (
      !bid?.vendor_feasibility_invite_id &&
      canViewField("actions") &&
      canOpenRfqModal &&
      ["in progress", "hold", "won"].includes(normalizedStatus(bid?.status))
    ) {
      list.push({ label: "", valueKey: "rfq_to_vendors" });
    }
    if (canViewField("created_by")) list.push({ label: "Created By", valueKey: "created_by" });
    if (canViewField("attachment_docs")) {
      list.push({ label: "Attachment", valueKey: "attachment_docs" });
    }
    if (canViewField("comments")) list.push({ label: "Comments", valueKey: "comments" });
    if (bid?.vendor_feasibility_invite_id && canViewField("vendor_feasibility_notes")) {
      list.push({ label: "General Notes", valueKey: "vendor_feasibility_notes" });
    }

    return list;
  }, [canOpenRfqModal, canViewField]);

  const bidDetails = useCallback((bid) => (Array.isArray(bid.details) ? bid.details : []), []);

  const vendorFeasibilityList = useCallback((bid) => {
    if (Array.isArray(bid?.vendor_feasibilities)) return bid.vendor_feasibilities;
    if (!bid?.vendor_feasibility_invite_id) return [];

    return [
      {
        row_key: bid.row_key || `vf-${bid.vendor_feasibility_invite_id}`,
        invite_id: bid.vendor_feasibility_invite_id,
        notes: bid.vendor_feasibility_notes,
        submitted_at: bid.vendor_feasibility_submitted_at,
        vendor: bid.vendor_feasibility_vendor,
        listing_vendor: listingVendorText(bid),
        details: bidDetails(bid),
      },
    ];
  }, [bidDetails]);

  const listFilterRows = useMemo(() => [...bids, ...vendorFeasibilities], [bids, vendorFeasibilities]);
  const listStudyTypeOptions = useMemo(
    () => projectTypes.length
      ? projectTypes
      : uniqueSortedListOptions(listFilterRows, listStudyTypeOptionFromBid),
    [listFilterRows, projectTypes]
  );
  const listProjectTypeOptions = useMemo(
    () => bidProjectTypeOptions.length
      ? bidProjectTypeOptions
      : uniqueSortedListOptions(listFilterRows, listProjectTypeOptionFromBid),
    [bidProjectTypeOptions, listFilterRows]
  );
  const listClientOptions = useMemo(
    () => clients.length
      ? clients
      : uniqueSortedListOptions(listFilterRows, listClientOptionFromBid),
    [clients, listFilterRows]
  );

  const openVendorFeasibilityDialog = useCallback((bid) => {
    if (!bid?.id) return;

    setVendorFeasibilityDialogBid(bid);
    setVendorFeasibilityDialogTab("details");
    setVendorTargetCpiOverrides({});
    setVendorFeasibilityDialogOpen(true);
    resetVendorFeasibilityFilters();
    setActionsAnchor(null);
    setActionsBid(null);
  }, [resetVendorFeasibilityFilters]);

  const toggleSelectedVendorFeasibility = useCallback((vendorFeasibility, checked) => {
    const key = vendorFeasibilityKey(vendorFeasibility);
    if (!key) return;

    setSelectedVendorFeasibilityKeys((current) => {
      const currentSet = new Set(current);

      if (checked) {
        currentSet.add(key);
      } else {
        currentSet.delete(key);
      }

      return [...currentSet];
    });
  }, []);

  const updateVendorTargetCpi = useCallback((vendorFeasibility, detail, value) => {
    const key = vendorFeasibilityDetailKey(vendorFeasibility, detail);
    if (!key) return;

    setVendorTargetCpiOverrides((current) => ({
      ...current,
      [key]: decimalInput(value, 3, 2),
    }));
  }, []);

  const submitVendorCountryWins = useCallback(async (groups, fallbackSuccessMessage) => {
    const bid = vendorFeasibilityDialogBid;
    const items = (Array.isArray(groups) ? groups : [])
      .flatMap((group) => {
        const vendorFeasibility = group.vendorFeasibility || group;
        const vendorId = vendorFeasibilityVendorId(vendorFeasibility);
        const inviteId = vendorFeasibility?.invite_id;
        const details = Array.isArray(group.details)
          ? group.details
          : vendorFeasibilityUnwonDetails(vendorFeasibility);

        if (!vendorId || !inviteId) return [];

        return details
          .filter((detail) => detail?.id && !detail.country_won)
          .map((detail) => {
            const item = {
              invite_id: Number(inviteId),
              vendor_id: Number(vendorId),
              bid_detail_id: Number(detail.id),
            };

            if (canViewAnyField("bid_cpi", "won_cpi")) {
              const targetCpiKey = vendorFeasibilityDetailKey(vendorFeasibility, detail);
              item.target_cpi = trimTrailingDecimal(
                vendorTargetCpiOverrides[targetCpiKey] ?? detail.target_cpi ?? ""
              );
            }

            return item;
          });
      })
      .filter((item) => item.invite_id && item.vendor_id && item.bid_detail_id);

    if (!bid?.id || items.length === 0) {
      showMessage("No pending vendor country rows were found to mark won.", "error");
      return;
    }

    try {
      setStatusSubmitLoading(true);
      const response = await fetch(`${API_URL}/bids/${bid.id}/vendor-country-wins`, {
        method: "POST",
        headers: authHeaders,
        body: JSON.stringify({ items }),
      });
      const data = await readJsonSafely(response);

      if (!response.ok || data.success !== 1) {
        throw new Error(apiErrorMessage(data, "Failed to mark vendor country won"));
      }

      const countryWins = Array.isArray(data.country_wins)
        ? data.country_wins
        : data.country_win
          ? [data.country_win]
          : [];
      const winsByInviteDetail = new Map();
      const winsByVendorDetail = new Map();

      countryWins.forEach((win) => {
        if (win?.invite_id && win?.bid_detail_id) {
          winsByInviteDetail.set(`${win.invite_id}-${win.bid_detail_id}`, win);
        }
        if (win?.vendor_id && win?.bid_detail_id) {
          winsByVendorDetail.set(`${win.vendor_id}-${win.bid_detail_id}`, win);
        }
      });

      const markCountryWinsOnBid = (currentBid) => {
        if (!currentBid || String(currentBid.id) !== String(bid.id)) return currentBid;

        const nextBid = {
          ...currentBid,
          ...(data.bid || {}),
        };
        const currentVendorFeasibilities = Array.isArray(currentBid.vendor_feasibilities)
          ? currentBid.vendor_feasibilities
          : [];

        return {
          ...nextBid,
          vendor_feasibilities: currentVendorFeasibilities.map((item) => {
            const inviteId = item?.invite_id;
            const vendorId = vendorFeasibilityVendorId(item);

            return {
              ...item,
              details: (Array.isArray(item.details) ? item.details : []).map((row) => {
                const win =
                  winsByInviteDetail.get(`${inviteId}-${row.id}`) ||
                  winsByVendorDetail.get(`${vendorId}-${row.id}`);

                return win
                  ? {
                      ...row,
                      country_won: true,
                      country_win_id: win.id,
                      country_won_at: win.won_at,
                      country_won_by: win.won_by,
                      target_cpi: win.target_cpi ?? row.target_cpi,
                    }
                  : row;
              }),
            };
          }),
        };
      };

      setVendorFeasibilityDialogBid((current) => markCountryWinsOnBid(current));
      setVendorFeasibilities((current) => current.map((item) => markCountryWinsOnBid(item)));
      setBids((current) =>
        current.map((item) =>
          String(item.id) === String(bid.id)
            ? {
                ...item,
                ...(data.bid || {}),
              }
            : item
        )
      );
      setSelectedVendorFeasibilityKeys([]);
      setVendorTargetCpiOverrides((current) => {
        const next = { ...current };
        countryWins.forEach((win) => {
          delete next[`${win?.invite_id || ""}-${win?.bid_detail_id || ""}`];
        });
        return next;
      });
      showMessage(apiSuccessMessage(data, fallbackSuccessMessage), "success");
    } catch (error) {
      showMessage(error.message || "Failed to mark vendor country won", "error");
    } finally {
      setStatusSubmitLoading(false);
    }
  }, [
    authHeaders,
    canViewAnyField,
    showMessage,
    vendorFeasibilityDialogBid,
    vendorTargetCpiOverrides,
  ]);

  const markVendorCountryWon = useCallback(
    (vendorFeasibility, detail) =>
      submitVendorCountryWins(
        [{ vendorFeasibility, details: [detail] }],
        "Vendor marked won for selected country successfully"
      ),
    [submitVendorCountryWins]
  );

  const markVendorAllCountriesWon = useCallback(
    (vendorFeasibility) =>
      submitVendorCountryWins(
        [{ vendorFeasibility, details: vendorFeasibilityUnwonDetails(vendorFeasibility) }],
        "Vendor marked won for all pending countries successfully"
      ),
    [submitVendorCountryWins]
  );

  const countryText = useCallback(
    (bid) =>
      bidDetails(bid)
        .map(countryDisplayValue)
        .filter(Boolean)
        .join(", "),
    [bidDetails]
  );

  const clientText = useCallback((bid) => {
    return clientDisplayName(bid);
  }, []);

  const projectManagerText = useCallback(
    (bid) => bid.project_manager_role?.name || bid.project_manager_text || "",
    []
  );

  const summaryValue = useCallback((bid, valueKey) => {
    if (valueKey === "project_type") return textOrDash(bid.project_type_master?.name || bid.project_type);
    if (valueKey === "client") return textOrDash(clientText(bid));
    if (valueKey === "project_launch_date") return formatDateTime(bid.project_launch_date);
    if (valueKey === "created_at") return formatDateTime(bid.created_at || bid.bid_creation_date);
    if (valueKey === "updated_at") return formatDateTime(bid.updated_at);
    if (valueKey === "created_by") return textOrDash(bid.created_by?.name);
    if (valueKey === "held_at") return formatDateTime(bid.held_at);
    if (valueKey === "held_by") return textOrDash(bid.held_by?.name);
    if (valueKey === "canceled_at") return formatDateTime(bid.canceled_at);
    if (valueKey === "canceled_by") return textOrDash(bid.canceled_by?.name);
    if (valueKey === "won_at") return formatDateTime(bid.won_at);
    if (valueKey === "won_by") return textOrDash(bid.won_by?.name);
    return textOrDash(bid[valueKey]);
  }, [clientText]);

  const mainCellValue = useCallback(
    (bid, key) => {
      if (key === "quest_code") {
        return (
          <SoftButton
            variant="text"
            color="info"
            size="small"
            onClick={() => {
              if (activeStatusTab === "vendor feasibility") {
                openVendorFeasibilityDialog(bid);
                return;
              }

              openBidDialogForBid(bid);
            }}
            sx={{
              minWidth: 0,
              p: 0,
              fontFamily: "Poppins, sans-serif",
              fontSize: "12px",
              lineHeight: 1.15,
              fontWeight: 600,
              textTransform: "none",
            }}
          >
            {textOrDash(bid.quest_code)}
          </SoftButton>
        );
      }
      if (key === "project_title") return textOrDash(bid.project_title);
      if (key === "project_manager") return textOrDash(projectManagerText(bid));
      if (key === "project_type") return textOrDash(bid.project_type_master?.name || bid.project_type);
      if (key === "project_type_option") return textOrDash(bid.project_type_option);
      if (key === "client") return textOrDash(clientText(bid));
      if (key === "listing_vendor") {
        if (activeStatusTab === "vendor feasibility") {
          return textOrDash(vendorFeasibilityCountText(bid));
        }

        const vendorCount = vendorFeasibilityList(bid).length;
        const vendorText = listingVendorText(bid);
        return vendorCount > 1 ? `${vendorText} (${vendorCount})` : textOrDash(vendorText);
      }
      if (key === "project_launch_date") return formatDateTime(bid.project_launch_date);
      if (key === "created_date") return formatDateTime(bid.created_at || bid.bid_creation_date);

      return "-";
    },
    [
      activeStatusTab,
      clientText,
      openBidDialogForBid,
      openVendorFeasibilityDialog,
      projectManagerText,
      vendorFeasibilityList,
    ]
  );

  const detailCellValue = useCallback((detail, key) => {
    if (key === "country") return textOrDash(countryDisplayValue(detail));
    if (key === "ir") return formatIrDisplay(detail.ir);
    return textOrDash(detail[key]);
  }, []);

  const toggleBidDetails = useCallback((bidId) => {
    setExpandedBidIds((current) =>
      current.includes(bidId) ? current.filter((id) => id !== bidId) : [...current, bidId]
    );
  }, []);

  const filteredBids = useMemo(() => {
    if (!visibleStatusTabs.some((tab) => tab.value === activeStatusTab)) return [];

    const search = canSearchBids ? listSearch.trim().toLowerCase() : "";
    const isVendorFeasibilityTab = activeStatusTab === "vendor feasibility";
    const baseRows =
      isVendorFeasibilityTab
        ? vendorFeasibilities
        : bids.filter((bid) => isBidInStatusTab(bid, activeStatusTab));
    const filteredRows = baseRows.filter((bid) => {
      if (!selectedListOptionsMatch(listStudyTypeFilter, listStudyTypeOptionFromBid(bid))) return false;
      if (!selectedListOptionsMatch(listProjectTypeFilter, listProjectTypeOptionFromBid(bid))) return false;
      if (!selectedListOptionsMatch(listClientFilter, listClientOptionFromBid(bid))) return false;

      if (!listStartDate && !listEndDate) return true;

      const dateValues = isVendorFeasibilityTab
        ? vendorFeasibilityList(bid).map((vendorFeasibility) => dateTimeFilterValue(vendorFeasibility.submitted_at))
        : [dateTimeFilterValue(bid.created_at || bid.bid_creation_date)];

      return dateValues.some((dateValue) => {
        if (!dateValue) return false;
        if (listStartDate && dateValue < listStartDate) return false;
        if (listEndDate && dateValue > listEndDate) return false;

        return true;
      });
    });

    if (!search) return filteredRows;

    return filteredRows.filter((bid) => {
      const details = bidDetails(bid);
      const vendorFeasibilitySearchFields = vendorFeasibilityList(bid).flatMap((vendorFeasibility) => [
        vendorFeasibility.listing_vendor,
        vendorFeasibility.notes,
        vendorFeasibility.submitted_at,
        vendorFeasibility.vendor?.name,
        vendorFeasibility.vendor?.company_name,
        vendorFeasibility.vendor?.email,
        ...(Array.isArray(vendorFeasibility.details) ? vendorFeasibility.details : []).flatMap((detail) => [
          detail.country_name,
          detail.country?.name,
          detail.targeting,
          detail.loi,
          detail.ir,
          detail.required_n,
          detail.bid_cpi,
          detail.won_cpi,
          detail.target_cpi,
          detail.feasibility,
          detail.required_field_time_days,
        ]),
      ]);
      const searchText = [
        bid.quest_code,
        bid.project_title,
        projectManagerText(bid),
        bid.project_manager?.email,
        bid.project_type_master?.name,
        bid.project_type,
        bid.project_type_option,
        clientText(bid),
        countryText(bid),
        bid.comments,
        bid.status,
        bid.created_by?.name,
        bid.updated_at,
        bid.held_by?.name,
        bid.canceled_by?.name,
        bid.won_by?.name,
        bid.listing_vendor,
        bid.vendor_feasibility_vendor?.name,
        bid.vendor_feasibility_vendor?.company_name,
        bid.vendor_feasibility_vendor?.email,
        bid.vendor_feasibility_notes,
        bid.vendor_feasibility_submitted_at,
        bid.latest_vendor_feasibility_submitted_at,
        bid.created_at,
        bid.held_at,
        bid.canceled_at,
        bid.won_at,
        ...details.flatMap((detail) => [
          detail.country_name,
          detail.country?.name,
          detail.targeting,
          detail.loi,
          detail.ir,
          detail.required_n,
          detail.bid_cpi,
          detail.won_cpi,
          detail.feasibility,
          detail.required_field_time_days,
        ]),
        ...vendorFeasibilitySearchFields,
      ]
        .filter((value) => value !== null && value !== undefined)
        .join(" ")
        .toLowerCase();

      return searchText.includes(search);
    });
  }, [
    activeStatusTab,
    bidDetails,
    bids,
    clientText,
    countryText,
    canSearchBids,
    listClientFilter,
    listEndDate,
    listProjectTypeFilter,
    listSearch,
    listStartDate,
    listStudyTypeFilter,
    projectManagerText,
    vendorFeasibilityList,
    vendorFeasibilities,
    visibleStatusTabs,
  ]);

  const listTotalPages = Math.max(Number(listPagination.last_page || 1), 1);
  const safeListPage = Math.min(listPage, listTotalPages);
  const paginatedBids = filteredBids;
  const listShowingFrom = Number(listPagination.from || 0);
  const listShowingTo = Number(listPagination.to || 0);

  useEffect(() => {
    setListPage(1);
  }, [
    activeStatusTab,
    listClientFilter,
    listEndDate,
    listProjectTypeFilter,
    listSearch,
    listStartDate,
    listStudyTypeFilter,
  ]);

  useEffect(() => {
    if (listPage > listTotalPages) {
      setListPage(listTotalPages);
    }
  }, [listPage, listTotalPages]);

  const exportColumns = useMemo(
    () => tableColumns.filter((column) => column.key !== "actions"),
    [tableColumns]
  );
  const includeExportStatus = canViewField("status");

  const exportCellValue = useCallback(
    (bid, key) => {
      if (key === "quest_code") return textOrDash(bid.quest_code);
      if (key === "project_title") return textOrDash(bid.project_title);
      if (key === "project_manager") return textOrDash(projectManagerText(bid));
      if (key === "project_type") return textOrDash(listStudyTypeOptionFromBid(bid)?.label);
      if (key === "project_type_option") return textOrDash(listProjectTypeOptionFromBid(bid)?.label);
      if (key === "client") return textOrDash(clientText(bid));
      if (key === "listing_vendor") {
        if (activeStatusTab === "vendor feasibility") return textOrDash(vendorFeasibilityCountText(bid));

        const vendorCount = vendorFeasibilityList(bid).length;
        const vendorText = listingVendorText(bid);
        return vendorCount > 1 ? `${vendorText} (${vendorCount})` : textOrDash(vendorText);
      }
      if (key === "project_launch_date") return formatDateTime(bid.project_launch_date);
      if (key === "created_date") return formatDateTime(bid.created_at || bid.bid_creation_date);

      return textOrDash(bid[key]);
    },
    [activeStatusTab, clientText, projectManagerText, vendorFeasibilityList]
  );

  const handleExportBids = useCallback(async () => {
    if (!listPagination.total) {
      showMessage("No data available to export.", "error");
      return;
    }

    try {
      setExportLoading(true);
      const exportParams = buildBidListParams(1, true);
      const exportResponse = await fetch(API_URL + '/bids?' + exportParams.toString(), { headers: authHeaders });
      const exportData = await readJsonSafely(exportResponse);
      if (!exportResponse.ok || exportData.success !== 1) {
        throw new Error(apiErrorMessage(exportData, 'Failed to fetch export data'));
      }
      const exportPayload = activeStatusTab === 'vendor feasibility'
        ? exportData.vendor_feasibilities || []
        : exportData.bids || [];
      const exportRows = Array.isArray(exportPayload.data) ? exportPayload.data : exportPayload;
      if (!Array.isArray(exportRows) || !exportRows.length) {
        showMessage('No data available to export.', 'error');
        return;
      }

      const headers = [
        ...exportColumns.map((column) => column.label),
        ...(includeExportStatus ? ["Status"] : []),
      ];
      const rows = exportRows.map((bid) => [
        ...exportColumns.map((column) => exportCellValue(bid, column.key)),
        ...(includeExportStatus ? [activeStatusTab] : []),
      ]);
      const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
      worksheet["!cols"] = headers.map((header, index) => ({
        wch: Math.max(
          String(header).length + 2,
          ...rows.map((row) => String(row[index] ?? "").length + 2),
          12
        ),
      }));
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Bids");

      const timestamp = new Date().toISOString().slice(0, 19).replace(/[-:T]/g, "");
      const filename = `bids-${exportFilenamePart(statusTabLabel(activeStatusTab))}-${timestamp}.xlsx`;

      XLSX.writeFile(workbook, filename);
      showMessage("Bids exported successfully.", "success");
    } catch (error) {
      showMessage(error.message || "Failed to export bids.", "error");
    } finally {
      setExportLoading(false);
    }
  }, [activeStatusTab, authHeaders, buildBidListParams, exportCellValue, exportColumns, includeExportStatus, listPagination.total, showMessage]);

  const localStatusCounts = useMemo(
    () =>
      visibleStatusTabs.reduce((counts, tab) => {
        counts[tab.value] =
          tab.value === "vendor feasibility"
            ? vendorFeasibilities.length
            : bids.filter((bid) => isBidInStatusTab(bid, tab.value)).length;
        return counts;
      }, {}),
    [bids, vendorFeasibilities, visibleStatusTabs]
  );

  const statusCounts = bidCounts;
  const visibleTableColumnCount = tableColumns.length || 1;
  const canViewSummaryDetails = canViewAnyField(
    "created_at",
    "created_by",
    "updated_at",
    "status",
    "attachment_docs",
    "comments"
  );
  const canExpandBidDetails =
    activeStatusTab !== "vendor feasibility" &&
    canExpandDetailsAction &&
    (detailColumns.length > 0 || canViewSummaryDetails);
  const isRfqMode = wonDialogMode === "rfq";
  const canSubmitWonDialog = isRfqMode && canSubmitRfq && canViewField("vendors");
  const canViewWonCountryDetails = canViewAnyField(
    "country",
    "loi",
    "ir",
    "bid_cpi",
    "won_cpi",
    "required_n",
    "feasibility",
    "required_field_time_days",
    "targeting"
  );
  const isVendorFeasibilityLockedWonMode = Boolean(wonDialogBid?.vendor_feasibility_vendor);
  const isVendorFeasibilitySubmittedWonMode =
    Array.isArray(wonDialogBid?.vendor_feasibility_vendor_options) &&
    wonDialogBid.vendor_feasibility_vendor_options.length > 0;
  const vendorFeasibilityDialogVendors = vendorFeasibilityList(vendorFeasibilityDialogBid);
  const vendorFeasibilityDialogVendorOptions = useMemo(() => {
    const optionsByValue = new Map();

    vendorFeasibilityDialogVendors.forEach((vendorFeasibility) => {
      const vendor = vendorFeasibility.vendor || {};
      const value = String(
        vendor.value || vendor.id || vendorFeasibility.vendor_id || vendorFeasibility.listing_vendor || ""
      );
      const label =
        vendorDisplayName(vendor) || stripVendorCodeFromLabel(vendorFeasibility.listing_vendor) || "-";

      if (value && !optionsByValue.has(value)) {
        optionsByValue.set(value, { value, label });
      }
    });

    return [...optionsByValue.values()].sort((first, second) => first.label.localeCompare(second.label));
  }, [vendorFeasibilityDialogVendors]);
  const filteredVendorFeasibilityDialogVendors = useMemo(() => {
    const search = vendorFeasibilitySearch.trim().toLowerCase();
    const selectedVendorValue = vendorFeasibilityVendorFilter?.value
      ? String(vendorFeasibilityVendorFilter.value)
      : "";

    return vendorFeasibilityDialogVendors.filter((vendorFeasibility) => {
      const vendor = vendorFeasibility.vendor || {};
      const vendorValue = String(
        vendor.value || vendor.id || vendorFeasibility.vendor_id || vendorFeasibility.listing_vendor || ""
      );
      const submittedDate = dateOnlyValue(vendorFeasibility.submitted_at);

      if (selectedVendorValue && vendorValue !== selectedVendorValue) return false;
      if (vendorFeasibilityStartDate && (!submittedDate || submittedDate < vendorFeasibilityStartDate)) return false;
      if (vendorFeasibilityEndDate && (!submittedDate || submittedDate > vendorFeasibilityEndDate)) return false;

      if (!search) return true;

      const searchText = [
        vendorDisplayName(vendor),
        vendorFeasibility.listing_vendor,
        vendor.name,
        vendor.company_name,
        vendor.email,
        vendorFeasibility.notes,
        vendorFeasibility.submitted_at,
        ...(Array.isArray(vendorFeasibility.details) ? vendorFeasibility.details : []).flatMap((detail) => [
          detail.country_name,
          detail.country?.name,
          detail.targeting,
          detail.loi,
          detail.ir,
          detail.required_n,
          detail.target_cpi,
          detail.feasibility,
          detail.required_field_time_days,
        ]),
      ]
        .filter((value) => value !== null && value !== undefined)
        .join(" ")
        .toLowerCase();

      return searchText.includes(search);
    });
  }, [
    vendorFeasibilityDialogVendors,
    vendorFeasibilityEndDate,
    vendorFeasibilitySearch,
    vendorFeasibilityStartDate,
    vendorFeasibilityVendorFilter,
  ]);
  const selectedVendorFeasibilityKeySet = useMemo(
    () => new Set(selectedVendorFeasibilityKeys),
    [selectedVendorFeasibilityKeys]
  );
  const selectedVendorFeasibilities = useMemo(
    () =>
      vendorFeasibilityDialogVendors.filter((vendorFeasibility) =>
        selectedVendorFeasibilityKeySet.has(vendorFeasibilityKey(vendorFeasibility))
      ),
    [selectedVendorFeasibilityKeySet, vendorFeasibilityDialogVendors]
  );
  const selectedVendorPendingCountryCount = useMemo(
    () =>
      selectedVendorFeasibilities.reduce(
        (total, vendorFeasibility) => total + vendorFeasibilityUnwonDetails(vendorFeasibility).length,
        0
      ),
    [selectedVendorFeasibilities]
  );
  const markSelectedVendorCountriesWon = useCallback(
    () =>
      submitVendorCountryWins(
        selectedVendorFeasibilities.map((vendorFeasibility) => ({
          vendorFeasibility,
          details: vendorFeasibilityUnwonDetails(vendorFeasibility),
        })),
        "Selected vendors marked won for all pending countries successfully"
      ),
    [selectedVendorFeasibilities, submitVendorCountryWins]
  );
  const vendorFeasibilityDialogBackendCount = Number(vendorFeasibilityDialogBid?.vendor_feasibility_count);
  const vendorFeasibilityDialogVendorCount = Number.isFinite(vendorFeasibilityDialogBackendCount)
    ? vendorFeasibilityDialogBackendCount
    : vendorFeasibilityDialogVendors.length;
  const vendorFeasibilityListingDetails = useMemo(() => {
    const bid = vendorFeasibilityDialogBid || {};
    const list = [];

    if (canViewField("quest_code")) {
      list.push({ label: "Project Code", value: textOrDash(bid.quest_code) });
    }
    if (canViewField("project_title")) {
      list.push({ label: "Project Title", value: textOrDash(bid.project_title) });
    }
    if (canViewField("project_manager_role")) {
      list.push({ label: "Project Management Team", value: textOrDash(projectManagerText(bid)) });
    }
    if (canViewField("project_type")) {
      list.push({ label: "Study Type", value: textOrDash(bid.project_type_master?.name || bid.project_type) });
      list.push({ label: "Project Type", value: textOrDash(bid.project_type_option) });
    }
    if (canViewAnyField("client_name", "client_code")) {
      list.push({ label: "Client Name", value: textOrDash(clientText(bid)) });
    }
    if (canViewAnyField("vendors", "listing_vendor")) {
      list.push({
        label: "Total Vendors",
        value: textOrDash(vendorFeasibilityDialogVendorCount),
      });
    }
    if (canViewField("project_launch_date")) {
      list.push({ label: "Project Launch Date", value: formatDateTime(bid.project_launch_date) });
    }
    if (canViewField("created_at")) {
      list.push({ label: "Created Date", value: formatDateTime(bid.created_at || bid.bid_creation_date) });
    }

    return list;
  }, [
    canViewAnyField,
    canViewField,
    clientText,
    projectManagerText,
    vendorFeasibilityDialogBid,
    vendorFeasibilityDialogVendorCount,
  ]);

  if (!isAuthReady || (canViewPage && loading)) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <SoftBox display="flex" justifyContent="center" alignItems="center" height="70vh">
          <FlagLoader />
        </SoftBox>
      </DashboardLayout>
    );
  }

  if (!canViewPage) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <SoftBox p={3}>
          <SoftTypography variant="h5" fontWeight="bold">
            Bid List
          </SoftTypography>
          <SoftTypography variant="body2" color="text" mt={1}>
            You do not have permission to view bids.
          </SoftTypography>
        </SoftBox>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <SoftSnackbar
        color={snackbar.color}
        icon="notifications"
        title="Bid"
        content={snackbar.message}
        open={snackbar.open}
        close={() => setSnackbar((current) => ({ ...current, open: false }))}
        onClose={() => setSnackbar((current) => ({ ...current, open: false }))}
        time={3000}
      />

      <SoftBox px={0} pt="2px" pb={1} mt="-14px">
        <SoftBox display="flex" justifyContent="space-between" alignItems="center" mb="2px">
          <SoftTypography
            variant="h4"
            fontWeight="bold"
            color="dark"
            sx={{ fontSize: "0.875rem" }}
          >
            Bid List
          </SoftTypography>
          {canOpenCreate && (
            <SoftButton
              color="info"
              size="small"
              startIcon={<Icon sx={{ fontSize: "0.8rem !important" }}>add</Icon>}
              onClick={openAddDialog}
              sx={{
                minHeight: 28,
                px: 1.25,
                py: "1px",
                mb: 0,
                fontSize: "10px",
                fontWeight: 400,
                textTransform: "none",
              }}
            >
              Add New Bid
            </SoftButton>
          )}
        </SoftBox>

        <Card>
          <SoftBox px="2px" pt="2px">
            <Tabs
              value={activeStatusTab}
              onChange={(_, value) => switchStatusTab(value)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                minHeight: 30,
                "& .MuiTabs-flexContainer": { gap: 1 },
                "& .MuiTabs-indicator": { display: "none" },
              }}
            >
              {visibleStatusTabs.map((tab) => {
                const tabStyle = statusTabStyles[tab.value] || statusTabStyles["in progress"];
                const unread = Number(bidUnreadTabs[tab.value] || 0);

                return (
                  <Tab
                    key={tab.value}
                    value={tab.value}
                    onClick={() => markBidTabNotificationsRead(tab.value)}
                    label={
                      <SoftBox component="span" display="inline-flex" alignItems="center" gap={0.4}>
                        <span>{tab.label} ({statusCounts[tab.value] || 0})</span>
                        {unread > 0 && (
                          <span
                            style={{
                              minWidth: 14,
                              height: 14,
                              padding: "0 4px",
                              borderRadius: 999,
                              background: "#ef4444",
                              color: "#ffffff",
                              fontSize: 9,
                              fontWeight: 400,
                              lineHeight: "14px",
                              textAlign: "center",
                            }}
                          >
                            {unread}
                          </span>
                        )}
                      </SoftBox>
                    }
                    sx={{
                      color: `${tabStyle.color} !important`,
                      backgroundColor: `${tabStyle.background} !important`,
                      border: `1px solid ${tabStyle.border}`,
                      borderRadius: "8px",
                      fontWeight: "400 !important",
                      minHeight: 28,
                      px: 2,
                      py: "1px",
                      fontSize: "0.875rem",
                      opacity: "1 !important",
                      textTransform: "none",
                      boxShadow: "none",
                      "&.Mui-selected, &.Mui-selected:hover": {
                        color: "#ffffff !important",
                        backgroundColor: `${tabStyle.color} !important`,
                        borderColor: tabStyle.color,
                        boxShadow: `0 8px 20px ${tabStyle.color}33`,
                      },
                      "&:hover": {
                        backgroundColor: `${tabStyle.background} !important`,
                        borderColor: tabStyle.color,
                      },
                    }}
                  />
                );
              })}
            </Tabs>
          </SoftBox>
          <SoftBox
            display="flex"
            justifyContent="space-between"
            alignItems={{ xs: "stretch", sm: "center" }}
            flexDirection={{ xs: "column", sm: "row" }}
            gap={0.75}
            px="2px"
            pt="2px"
            pb="4px"
          >
            <SoftBox display="flex" alignItems="center" gap={0.5} flexWrap="wrap">
              <SoftBox color="text" fontSize="12px" fontWeight={400}>
                Showing {listShowingFrom} to {listShowingTo} of {listPagination.total} entries
              </SoftBox>
              {listTotalPages > 1 && (
                <SoftBox display="flex" alignItems="center" gap={0.15}>
                  <IconButton
                    size="small"
                    disabled={safeListPage <= 1}
                    onClick={() => setListPage(1)}
                    sx={{ p: 0.2 }}
                  >
                    <Icon sx={{ fontSize: "1rem !important" }}>first_page</Icon>
                  </IconButton>
                  <IconButton
                    size="small"
                    disabled={safeListPage <= 1}
                    onClick={() => setListPage((current) => Math.max(current - 1, 1))}
                    sx={{ p: 0.2 }}
                  >
                    <Icon sx={{ fontSize: "1rem !important" }}>chevron_left</Icon>
                  </IconButton>
                  <SoftBox
                    sx={{
                      minWidth: 46,
                      px: 0.5,
                      py: 0.15,
                      border: "1px solid #d8dee9",
                      borderRadius: "5px",
                      color: "#475569",
                      fontSize: "10px",
                      textAlign: "center",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {safeListPage} / {listTotalPages}
                  </SoftBox>
                  <IconButton
                    size="small"
                    disabled={safeListPage >= listTotalPages}
                    onClick={() => setListPage((current) => Math.min(current + 1, listTotalPages))}
                    sx={{ p: 0.2 }}
                  >
                    <Icon sx={{ fontSize: "1rem !important" }}>chevron_right</Icon>
                  </IconButton>
                  <IconButton
                    size="small"
                    disabled={safeListPage >= listTotalPages}
                    onClick={() => setListPage(listTotalPages)}
                    sx={{ p: 0.2 }}
                  >
                    <Icon sx={{ fontSize: "1rem !important" }}>last_page</Icon>
                  </IconButton>
                </SoftBox>
              )}
            </SoftBox>
            {(canExportBids ||
              canSearchBids ||
              canViewField("created_at") ||
              canViewField("project_type") ||
              canViewAnyField("client_name", "client_code")) && (
              <SoftBox
                display="flex"
                flexWrap="wrap"
                gap={0.75}
                justifyContent={{ xs: "flex-start", sm: "flex-end" }}
                sx={{ ml: { sm: "auto" }, width: { xs: "100%", sm: "auto" } }}
              >
                {canSearchBids && (
                  <SoftBox sx={listSearchControlSx}>
                    <SoftInput
                      size="small"
                      placeholder="Search..."
                      value={listSearch}
                      onChange={(event) => setListSearch(event.target.value)}
                      sx={listSearchInputSx}
                    />
                  </SoftBox>
                )}
                {canViewField("created_at") && (
                  <>
                    <SoftBox sx={listSearchControlSx}>
                      <SoftInput
                        size="small"
                        type="datetime-local"
                        value={listStartDate}
                        onChange={(event) => setListStartDate(event.target.value)}
                        sx={listSearchInputSx}
                        inputProps={{ "aria-label": "From date and time" }}
                      />
                    </SoftBox>
                    <SoftBox sx={listSearchControlSx}>
                      <SoftInput
                        size="small"
                        type="datetime-local"
                        value={listEndDate}
                        onChange={(event) => setListEndDate(event.target.value)}
                        sx={listSearchInputSx}
                        inputProps={{ "aria-label": "To date and time" }}
                      />
                    </SoftBox>
                  </>
                )}
                {canViewField("project_type") && (
                  <>
                    <SoftBox sx={listSearchControlSx}>
                      <SoftSelect
                        size="small"
                        placeholder="Study Type"
                        value={listStudyTypeFilter}
                        options={listStudyTypeOptions}
                        onChange={(value) => setListStudyTypeFilter(Array.isArray(value) ? value : [])}
                        isMulti
                        closeMenuOnSelect={false}
                        isSearchable
                        isClearable
                        sx={listSearchSelectSx}
                      />
                    </SoftBox>
                    <SoftBox sx={listSearchControlSx}>
                      <SoftSelect
                        size="small"
                        placeholder="Project Type"
                        value={listProjectTypeFilter}
                        options={listProjectTypeOptions}
                        onChange={(value) => setListProjectTypeFilter(Array.isArray(value) ? value : [])}
                        isMulti
                        closeMenuOnSelect={false}
                        isSearchable
                        isClearable
                        sx={listSearchSelectSx}
                      />
                    </SoftBox>
                  </>
                )}
                {canViewAnyField("client_name", "client_code") && (
                  <SoftBox sx={listSearchControlSx}>
                    <SoftSelect
                      size="small"
                      placeholder="Client"
                      value={listClientFilter}
                      options={listClientOptions}
                      onChange={(value) => setListClientFilter(Array.isArray(value) ? value : [])}
                      isMulti
                      closeMenuOnSelect={false}
                      isSearchable
                      isClearable
                      sx={listSearchSelectSx}
                    />
                  </SoftBox>
                )}
                {canExportBids && (
                  <SoftButton
                    color="info"
                    variant="outlined"
                    onClick={handleExportBids}
                    disabled={exportLoading || !listPagination.total}
                    sx={listExportButtonSx}
                  >
                    <Icon sx={{ mr: 0.5 }}>download</Icon>
                    {exportLoading ? "Exporting..." : "Export"}
                  </SoftButton>
                )}
              </SoftBox>
            )}
          </SoftBox>

          <TableContainer
            component={Paper}
            sx={{
              boxShadow: "none",
              overflowX: "hidden",
              width: "100%",
              borderTop: "1px solid #edf2f7",
            }}
          >
            <Table size="small" sx={{ width: "100%", tableLayout: "fixed" }}>
              <TableHead sx={{ display: "table-header-group" }}>
                <TableRow>
                  {tableColumns.map((column) => (
                    <TableCell
                      key={column.key}
                      sx={tableHeaderCellSx}
                      align={column.align || "left"}
                      width={column.width}
                    >
                      {column.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredBids.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={visibleTableColumnCount} sx={tableBodyCellSx} align="center">
                      No bids found.
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedBids.map((bid) => {
                    const rowKey = bid.row_key || bid.id;
                    const details = bidDetails(bid);
                    const isExpanded = expandedBidIds.includes(rowKey);
                    const detailSummaryFields = detailSummaryFieldsForBid(bid);
                    const statusActions = statusActionsForBid(bid);

                    return (
                      <Fragment key={rowKey}>
                        <TableRow hover sx={{ "&:hover": { backgroundColor: "#f8fafc" } }}>
                          {tableColumns.map((column) => (
                            <TableCell
                              key={column.key}
                              sx={{
                                ...tableBodyCellSx,
                                whiteSpace: column.key === "actions" ? "nowrap" : "normal",
                                overflowWrap: "anywhere",
                              }}
                              align={column.align || "left"}
                              width={column.width}
                            >
                              {column.key === "actions" ? (
                                <SoftBox display="flex" justifyContent="center" alignItems="center" gap={0.15}>
                                  {canExpandBidDetails && (
                                    <Tooltip title={isExpanded ? "Hide details" : "View details"}>
                                      <IconButton
                                        size="small"
                                        onClick={() => toggleBidDetails(rowKey)}
                                        sx={{ p: 0.25 }}
                                      >
                                        <Icon sx={{ fontSize: "0.95rem !important" }}>
                                          {isExpanded ? "keyboard_arrow_up" : "keyboard_arrow_down"}
                                        </Icon>
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                  {canOpenStatusMenu && statusActions.length > 0 && (
                                    <Tooltip title="Bid actions">
                                      <span>
                                        <IconButton
                                          size="small"
                                          disabled={statusSubmitLoading}
                                          onClick={(event) => openActionsMenu(event, bid)}
                                          sx={{ p: 0.25 }}
                                        >
                                          <Icon sx={{ fontSize: "0.95rem !important" }}>more_vert</Icon>
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                  )}
                                </SoftBox>
                              ) : (
                                <SoftBox
                                  sx={{
                                    minWidth: 0,
                                    fontFamily: "Poppins, sans-serif",
                                    fontSize: "12px",
                                    fontWeight: 400,
                                    lineHeight: 1.15,
                                    overflowWrap: "anywhere",
                                  }}
                                >
                                  {mainCellValue(bid, column.key)}
                                </SoftBox>
                              )}
                            </TableCell>
                          ))}
                        </TableRow>

                        {isExpanded && canExpandBidDetails && (
                          <TableRow>
                            <TableCell
                              colSpan={visibleTableColumnCount}
                              sx={{ ...tableBodyCellSx, backgroundColor: "#f8fafc", p: 1 }}
                            >
                              <SoftBox sx={expandedDetailShellSx}>
                                <SoftBox sx={expandedPanelSx}>
                                  <SoftBox sx={expandedPanelHeaderSx}>
                                    <SoftTypography variant="button" fontWeight="bold" color="dark">
                                      Bid Summary
                                    </SoftTypography>
                                    <SoftTypography
                                      variant="caption"
                                      color="text"
                                      sx={{ fontFamily: "Poppins, sans-serif", fontSize: "12px" }}
                                    >
                                      {textOrDash(bid.quest_code)}
                                    </SoftTypography>
                                  </SoftBox>
                                  {detailSummaryFields.length > 0 && (
                                    <SoftBox sx={expandedSummaryGridSx}>
                                      {detailSummaryFields.map((field) => {
                                        const iconName =
                                          field.valueKey === "rfq_to_vendors"
                                            ? "send"
                                            : field.valueKey === "created_by"
                                              ? "person"
                                              : field.valueKey === "attachment_docs"
                                                ? "attach_file"
                                                : "notes";

                                        return (
                                          <SoftBox
                                            key={field.valueKey}
                                            sx={{
                                              ...expandedSummaryItemSx,
                                              ...(field.valueKey === "rfq_to_vendors"
                                                ? {
                                                    gridColumn: { sm: "1 / -1" },
                                                    backgroundColor: "#f0f9ff",
                                                    borderColor: "#bae6fd",
                                                  }
                                                : {}),
                                            }}
                                          >
                                            <SoftBox sx={expandedSummaryLabelSx}>
                                              <Icon>{iconName}</Icon>
                                              <SoftTypography variant="caption" fontWeight="bold" color="text">
                                                {field.label || "Vendor RFQ"}
                                              </SoftTypography>
                                            </SoftBox>
                                            {field.valueKey === "attachment_docs" ? (
                                              renderAttachmentLinks(bid.attachment_docs)
                                            ) : field.valueKey === "rfq_to_vendors" ? (
                                              <SoftButton
                                                color="info"
                                                size="small"
                                                variant="gradient"
                                                startIcon={<Icon>send</Icon>}
                                                onClick={() => handleRfqToVendors(bid)}
                                                sx={{ ...tabSizedButtonSx, px: 2.5 }}
                                              >
                                                RFQ To Vendors
                                              </SoftButton>
                                            ) : (
                                              <SoftTypography
                                                variant="body2"
                                                color="dark"
                                                sx={{ ...detailSummaryTextSx, overflowWrap: "anywhere" }}
                                              >
                                                {summaryValue(bid, field.valueKey)}
                                              </SoftTypography>
                                            )}
                                          </SoftBox>
                                        );
                                      })}
                                    </SoftBox>
                                  )}
                                </SoftBox>

                                <SoftBox sx={expandedPanelSx}>
                                  <SoftBox sx={expandedPanelHeaderSx}>
                                    <SoftTypography variant="button" fontWeight="bold" color="dark">
                                      Country Details
                                    </SoftTypography>
                                    <SoftTypography
                                      variant="caption"
                                      color="text"
                                      sx={{ fontFamily: "Poppins, sans-serif", fontSize: "12px" }}
                                    >
                                      {details.length} {details.length === 1 ? "country" : "countries"}
                                    </SoftTypography>
                                  </SoftBox>
                                  <SoftBox sx={expandedMobileDetailsSx}>
                                    {details.length === 0 ? (
                                      <SoftTypography variant="body2" color="text" sx={detailSummaryTextSx}>
                                        No country details found.
                                      </SoftTypography>
                                    ) : (
                                      details.map((detail, detailIndex) => (
                                        <SoftBox
                                          key={detail.id || `mobile-detail-${rowKey}-${detailIndex}`}
                                          sx={expandedMobileDetailCardSx}
                                        >
                                          <SoftBox sx={{ ...expandedPanelHeaderSx, py: 0.5, px: 0.75 }}>
                                            <SoftTypography variant="caption" fontWeight="bold" color="dark">
                                              {detailCellValue(detail, "country")}
                                            </SoftTypography>
                                            <SoftTypography
                                              variant="caption"
                                              color="text"
                                              sx={{ fontFamily: "Poppins, sans-serif", fontSize: "12px" }}
                                            >
                                              #{detailIndex + 1}
                                            </SoftTypography>
                                          </SoftBox>
                                          <SoftBox sx={expandedMobileDetailGridSx}>
                                            {detailColumns
                                              .filter((column) => column.key !== "country")
                                              .map((column) => (
                                                <SoftBox key={column.key} minWidth={0}>
                                                  <SoftTypography
                                                    variant="caption"
                                                    fontWeight="bold"
                                                    color="text"
                                                    sx={{ fontSize: "9px", display: "block" }}
                                                  >
                                                    {column.label}
                                                  </SoftTypography>
                                                  {column.key === "targeting" ? (
                                                    detail.targeting ? (
                                                      <SoftBox
                                                        sx={{
                                                          ...richTextDisplaySx,
                                                          fontSize: "9.5px",
                                                          overflowWrap: "anywhere",
                                                        }}
                                                        dangerouslySetInnerHTML={{
                                                          __html: sanitizeRichHtml(detail.targeting),
                                                        }}
                                                      />
                                                    ) : (
                                                      <SoftTypography
                                                        variant="caption"
                                                        color="dark"
                                                        sx={{ fontSize: "9.5px" }}
                                                      >
                                                        -
                                                      </SoftTypography>
                                                    )
                                                  ) : (
                                                    <SoftTypography
                                                      variant="caption"
                                                      color="dark"
                                                      sx={{ fontSize: "9.5px", overflowWrap: "anywhere" }}
                                                    >
                                                      {detailCellValue(detail, column.key)}
                                                    </SoftTypography>
                                                  )}
                                                </SoftBox>
                                              ))}
                                          </SoftBox>
                                        </SoftBox>
                                      ))
                                    )}
                                  </SoftBox>
                                  <TableContainer component={Paper} sx={expandedTableContainerSx}>
                                    <Table size="small">
                                      <TableHead sx={{ display: "table-header-group" }}>
                                        <TableRow>
                                          {detailColumns.map((column) => (
                                            <TableCell key={column.key} sx={detailHeaderCellSx} width={column.width}>
                                              {column.label}
                                            </TableCell>
                                          ))}
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {details.length === 0 ? (
                                          <TableRow>
                                            <TableCell colSpan={detailColumns.length || 1} sx={detailBodyCellSx}>
                                              No country details found.
                                            </TableCell>
                                          </TableRow>
                                        ) : (
                                          details.map((detail, detailIndex) => (
                                            <TableRow key={detail.id || `detail-${rowKey}-${detailIndex}`}>
                                              {detailColumns.map((column) => (
                                                <TableCell key={column.key} sx={detailBodyCellSx} width={column.width}>
                                                  {column.key === "targeting" ? (
                                                    detail.targeting ? (
                                                      <SoftBox
                                                        sx={richTextDisplaySx}
                                                        dangerouslySetInnerHTML={{
                                                          __html: sanitizeRichHtml(detail.targeting),
                                                        }}
                                                      />
                                                    ) : (
                                                      "-"
                                                    )
                                                  ) : (
                                                    detailCellValue(detail, column.key)
                                                  )}
                                                </TableCell>
                                              ))}
                                            </TableRow>
                                          ))
                                        )}
                                      </TableBody>
                                    </Table>
                                  </TableContainer>
                                </SoftBox>
                              </SoftBox>
                            </TableCell>
                          </TableRow>
                        )}
                      </Fragment>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>

        <Menu
          anchorEl={actionsAnchor}
          open={Boolean(actionsAnchor)}
          onClose={closeActionsMenu}
          PaperProps={{
            sx: {
              minWidth: 170,
              borderRadius: "8px",
              boxShadow: "0 12px 32px rgba(20, 20, 43, 0.18)",
              py: 1,
            },
          }}
        >
          {statusActionsForBid(actionsBid).map((action) => {
            const meta = statusActionMeta[action.status] || statusActionMeta["in progress"];

            return (
              <MenuItem
                key={action.status}
                disabled={statusSubmitLoading}
                onClick={() => requestStatusChange(actionsBid, action.status)}
                sx={{ color: meta.color, fontWeight: 600, fontSize: "1rem" }}
              >
                {meta.label}
              </MenuItem>
            );
          })}
        </Menu>
      </SoftBox>

      <Dialog
        open={confirmStatusDialog.open}
        onClose={closeConfirmStatusDialog}
        PaperProps={{ sx: responsiveBidDialogPaperSx }}
      >
        <DialogTitle>Confirm Status Change</DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <SoftTypography variant="body2" color="text">
            Are you sure you want to move this bid to{" "}
            <strong>
              {(statusActionMeta[confirmStatusDialog.status] || statusActionMeta["in progress"]).label}
            </strong>
            ?
          </SoftTypography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <SoftButton
            color="dark"
            variant="outlined"
            onClick={closeConfirmStatusDialog}
            disabled={statusSubmitLoading}
          >
            Cancel
          </SoftButton>
          <SoftButton
            color={(statusActionMeta[confirmStatusDialog.status] || statusActionMeta["in progress"]).buttonColor}
            onClick={confirmStatusChange}
            disabled={statusSubmitLoading}
            endIcon={statusSubmitLoading ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {statusSubmitLoading
              ? "Processing..."
              : `Confirm ${(statusActionMeta[confirmStatusDialog.status] || statusActionMeta["in progress"]).label}`}
          </SoftButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={vendorFeasibilityDialogOpen}
        onClose={closeVendorFeasibilityDialog}
        PaperProps={{ sx: responsiveBidDialogPaperSx }}
      >
        <DialogTitle sx={{ textAlign: "center", fontSize: "22px" }}>
          Vendor Feasibility
          <SoftBox mt={1} color="primary" fontWeight="medium" sx={{ fontSize: "18px", textAlign: "left" }}>
            <span style={{ color: "#344767" }}>Project Code:</span>{" "}
            {textOrDash(vendorFeasibilityDialogBid?.quest_code)} |{" "}
            <span style={{ color: "#344767" }}>Total Vendors:</span>{" "}
            {vendorFeasibilityDialogVendorCount}
          </SoftBox>
        </DialogTitle>
        <Tabs
          value={vendorFeasibilityDialogTab}
          onChange={(event, value) => setVendorFeasibilityDialogTab(value)}
          centered
          sx={{ height: "35px", minHeight: "35px" }}
        >
          <Tab label="Details" value="details" sx={{ minHeight: "35px" }} />
          <Tab label="Vendors" value="vendors" sx={{ minHeight: "35px" }} />
        </Tabs>

        <DialogContent>
          {vendorFeasibilityDialogTab === "details" && (
            <SoftBox>
              <SoftBox
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "repeat(2, minmax(0, 1fr))",
                    lg: "repeat(4, minmax(0, 1fr))",
                  },
                  gap: 1,
                }}
              >
                {vendorFeasibilityListingDetails.map((item) => (
                  <SoftBox key={item.label} minWidth={0}>
                    <SoftTypography variant="caption" fontWeight="bold" color="text">
                      {item.label}
                    </SoftTypography>
                    <SoftTypography
                      variant="body2"
                      color="text"
                      sx={{ ...detailSummaryTextSx, overflowWrap: "anywhere" }}
                    >
                      {item.value}
                    </SoftTypography>
                  </SoftBox>
                ))}
              </SoftBox>
            </SoftBox>
          )}

          {vendorFeasibilityDialogTab === "vendors" && (
            <SoftBox display="flex" flexDirection="column" gap={1}>
              {vendorFeasibilityDialogVendors.length > 0 && (
                <SoftBox display="flex" flexWrap="wrap" gap={0.75} justifyContent="space-between">
                  <SoftBox display="flex" flexWrap="wrap" gap={0.75}>
                    {canOpenWonModal && canSubmitWon && (
                      <SoftButton
                        color={selectedVendorPendingCountryCount === 0 ? "success" : "info"}
                        size="small"
                        disabled={statusSubmitLoading || selectedVendorPendingCountryCount === 0}
                        onClick={markSelectedVendorCountriesWon}
                        sx={compactTableButtonSx}
                      >
                        Won Selected Vendors
                      </SoftButton>
                    )}
                  </SoftBox>
                  <SoftBox display="flex" flexWrap="wrap" gap={0.75} justifyContent="flex-end">
                    <SoftBox sx={listSearchControlSx}>
                      <SoftInput
                        size="small"
                        placeholder="Search..."
                        value={vendorFeasibilitySearch}
                        onChange={(event) => setVendorFeasibilitySearch(event.target.value)}
                        sx={listSearchInputSx}
                      />
                    </SoftBox>
                    <SoftBox sx={listSearchControlSx}>
                      <SoftInput
                        size="small"
                        type="date"
                        value={vendorFeasibilityStartDate}
                        onChange={(event) => setVendorFeasibilityStartDate(event.target.value)}
                        sx={listSearchInputSx}
                        inputProps={{ "aria-label": "Start Date" }}
                      />
                    </SoftBox>
                    <SoftBox sx={listSearchControlSx}>
                      <SoftInput
                        size="small"
                        type="date"
                        value={vendorFeasibilityEndDate}
                        onChange={(event) => setVendorFeasibilityEndDate(event.target.value)}
                        sx={listSearchInputSx}
                        inputProps={{ "aria-label": "End Date" }}
                      />
                    </SoftBox>
                    <SoftBox sx={listSearchControlSx}>
                      <SoftSelect
                        size="small"
                        placeholder="Vendor"
                        value={vendorFeasibilityVendorFilter}
                        options={vendorFeasibilityDialogVendorOptions}
                        onChange={setVendorFeasibilityVendorFilter}
                        isSearchable
                        isClearable
                        sx={listSearchSelectSx}
                      />
                    </SoftBox>
                  </SoftBox>
                </SoftBox>
              )}

              {vendorFeasibilityDialogVendors.length === 0 ? (
                <SoftTypography variant="body2" color="text">
                  No vendor feasibility submissions found.
                </SoftTypography>
              ) : filteredVendorFeasibilityDialogVendors.length === 0 ? (
                <SoftTypography variant="body2" color="text">
                  No vendor feasibility submissions match the selected filters.
                </SoftTypography>
              ) : (
                filteredVendorFeasibilityDialogVendors.map((vendorFeasibility, vendorIndex) => {
                  const vendor = vendorFeasibility.vendor || {};
                  const vendorName =
                    vendorDisplayName(vendor) || stripVendorCodeFromLabel(vendorFeasibility.listing_vendor) || "-";
                  const submittedDetails = Array.isArray(vendorFeasibility.details)
                    ? vendorFeasibility.details
                    : [];
                  const vendorKey = vendorFeasibilityKey(vendorFeasibility);
                  const pendingDetails = vendorFeasibilityUnwonDetails(vendorFeasibility);
                  const isVendorSelected = selectedVendorFeasibilityKeySet.has(vendorKey);
                  const canBulkWinVendor = canOpenWonModal && canSubmitWon;

                  return (
                    <SoftBox
                      key={vendorFeasibility.row_key || vendorFeasibility.invite_id || `vendor-${vendorIndex}`}
                      p={1}
                      border="1px solid #d2d6da"
                      borderRadius="6px"
                      sx={{ backgroundColor: "#ffffff" }}
                    >
                      <SoftBox
                        mb={0.75}
                        display="flex"
                        alignItems="flex-start"
                        justifyContent="space-between"
                        gap={1}
                      >
                        <SoftBox display="flex" alignItems="flex-start" gap={0.5} minWidth={0}>
                          {canBulkWinVendor && (
                            <Tooltip title="Select vendor">
                              <span>
                                <Checkbox
                                  size="small"
                                  checked={isVendorSelected}
                                  disabled={statusSubmitLoading || pendingDetails.length === 0}
                                  onChange={(event) =>
                                    toggleSelectedVendorFeasibility(vendorFeasibility, event.target.checked)
                                  }
                                  sx={{ p: 0, mt: 0.1 }}
                                />
                              </span>
                            </Tooltip>
                          )}
                          <SoftBox minWidth={0}>
                            <SoftTypography variant="button" fontWeight="bold" color="dark">
                              Vendor {vendorIndex + 1}: {textOrDash(vendorName)}
                            </SoftTypography>
                            <SoftTypography variant="caption" color="text" display="block">
                              {[
                                vendor.email ? `Email: ${vendor.email}` : null,
                                vendorFeasibility.submitted_at
                                  ? `Submitted: ${formatDateTime(vendorFeasibility.submitted_at)}`
                                  : null,
                              ]
                                .filter(Boolean)
                                .join(" | ")}
                            </SoftTypography>
                          </SoftBox>
                        </SoftBox>
                        {canBulkWinVendor && (
                          <SoftButton
                            color={pendingDetails.length === 0 ? "success" : "info"}
                            size="small"
                            disabled={statusSubmitLoading || pendingDetails.length === 0}
                            onClick={() => markVendorAllCountriesWon(vendorFeasibility)}
                            sx={compactTableButtonSx}
                          >
                            Won All
                          </SoftButton>
                        )}
                      </SoftBox>

                      {canViewField("vendor_feasibility_notes") && vendorFeasibility.notes && (
                        <SoftBox mb={0.75}>
                          <SoftTypography variant="caption" fontWeight="bold" color="text">
                            General Notes
                          </SoftTypography>
                          <SoftTypography variant="body2" color="text" sx={detailSummaryTextSx}>
                            {vendorFeasibility.notes}
                          </SoftTypography>
                        </SoftBox>
                      )}

                      <TableContainer component={Paper} sx={{ boxShadow: "none", overflowX: "auto" }}>
                        <Table size="small" sx={{ width: "100%", tableLayout: "fixed" }}>
                          <TableHead sx={{ display: "table-header-group" }}>
                            <TableRow>
                              {vendorFeasibilitySubmittedColumns.map((column) => (
                                <TableCell key={column.key} sx={detailHeaderCellSx} width={column.width}>
                                  {column.label}
                                </TableCell>
                              ))}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {submittedDetails.length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={vendorFeasibilitySubmittedColumns.length || 1} sx={detailBodyCellSx}>
                                  No country feasibility submitted.
                                </TableCell>
                              </TableRow>
                            ) : (
                              submittedDetails.map((detail, detailIndex) => (
                                <TableRow key={detail.id || `vendor-detail-${vendorIndex}-${detailIndex}`}>
                                  {vendorFeasibilitySubmittedColumns.map((column) => (
                                    <TableCell key={column.key} sx={detailBodyCellSx} width={column.width}>
                                      {column.key === "country_won_action" ? (
                                        <SoftButton
                                          color={detail.country_won ? "success" : "info"}
                                          size="small"
                                          disabled={statusSubmitLoading || detail.country_won}
                                          onClick={() => markVendorCountryWon(vendorFeasibility, detail)}
                                          sx={compactTableButtonSx}
                                        >
                                          Won
                                        </SoftButton>
                                      ) : column.key === "targeting" ? (
                                        detail.targeting ? (
                                          <SoftBox
                                            sx={richTextDisplaySx}
                                            dangerouslySetInnerHTML={{
                                              __html: sanitizeRichHtml(detail.targeting),
                                            }}
                                          />
                                        ) : (
                                          "-"
                                        )
                                      ) : column.key === "target_cpi" ? (
                                        <SoftInput
                                          size="small"
                                          type="text"
                                          inputProps={{ inputMode: "decimal", maxLength: 6 }}
                                          value={
                                            vendorTargetCpiOverrides[
                                              vendorFeasibilityDetailKey(vendorFeasibility, detail)
                                            ] ?? detail.target_cpi ?? ""
                                          }
                                          disabled={statusSubmitLoading}
                                          onChange={(event) =>
                                            updateVendorTargetCpi(
                                              vendorFeasibility,
                                              detail,
                                              event.target.value
                                            )
                                          }
                                          sx={listSearchInputSx}
                                        />
                                      ) : (
                                        detailCellValue(detail, column.key)
                                      )}
                                    </TableCell>
                                  ))}
                                </TableRow>
                              ))
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </SoftBox>
                  );
                })
              )}
            </SoftBox>
          )}
        </DialogContent>
        <DialogActions sx={compactBidDialogActionsSx}>
          <SoftButton
            color="dark"
            variant="outlined"
            onClick={closeVendorFeasibilityDialog}
            disabled={statusSubmitLoading}
            sx={tabSizedButtonSx}
          >
            Close
          </SoftButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={addDialogOpen}
        onClose={closeAddDialog}
        PaperProps={{ sx: responsiveBidDialogPaperSx }}
      >
        <DialogTitle sx={compactBidDialogTitleSx}>
          {isBidDialogCreate
            ? "Add New Bid"
            : isBidDialogWon
              ? "Final Edit Bid"
              : isBidDialogEdit
                ? "Edit Bid"
                : "View Bid"}
        </DialogTitle>
        <DialogContent sx={compactBidDialogContentSx}>
          <Grid container columnSpacing={1.25} rowSpacing={0.375}>
            {canShowBidDialogField("project_title") && (
              <Grid item xs={12} sm={6} lg={3}>
                <SoftTypography variant="caption" fontWeight="bold">
                  Project Title *
                </SoftTypography>
                <SoftInput
                  value={form.project_title}
                  placeholder="Project Title"
                  disabled={isBidDialogReadOnly}
                  onChange={(event) => updateForm("project_title", event.target.value)}
                />
              </Grid>
            )}

            {canShowBidDialogField("project_manager_role") && (
              <Grid item xs={12} sm={6} lg={3}>
                <SoftTypography variant="caption" fontWeight="bold">
                  Project Management Team *
                </SoftTypography>
                <SoftSelect
                  {...compactSoftSelectProps}
                  placeholder="Select Project Management Team"
                  value={form.project_manager_role}
                  options={projectManagerRoles}
                  isLoading={projectManagersLoading}
                  isDisabled={isBidDialogReadOnly}
                  onMenuOpen={fetchProjectManagers}
                  onChange={(value) => updateForm("project_manager_role", value)}
                  isSearchable
                  isClearable
                />
              </Grid>
            )}

            {canShowBidDialogField("project_type") && (
              <Grid item xs={12} sm={6} lg={3}>
                <SoftTypography variant="caption" fontWeight="bold">
                  Study Type *
                </SoftTypography>
                <SoftBox display="flex" alignItems="center" gap={0.5}>
                  <SoftBox flex={1}>
                    <SoftSelect
                      {...compactSoftSelectProps}
                      placeholder="Select Study Type"
                      value={form.project_type}
                      options={projectTypes}
                      isLoading={projectTypesLoading}
                      isDisabled={isBidDialogReadOnly}
                      onMenuOpen={fetchProjectTypes}
                      onChange={(value) => updateForm("project_type", value)}
                      isSearchable
                      isClearable
                    />
                  </SoftBox>
                  {canOpenProjectTypeModal && (
                    <Tooltip title="Add study type">
                      <IconButton
                        color="info"
                        size="small"
                        disabled={isBidDialogReadOnly}
                        onClick={() => {
                          setNewProjectType("");
                          setProjectTypeDialogOpen(true);
                        }}
                        sx={compactFieldActionButtonSx}
                      >
                        <Icon fontSize="small">add</Icon>
                      </IconButton>
                    </Tooltip>
                  )}
                </SoftBox>
              </Grid>
            )}

            {canShowBidDialogField("project_type") && (
              <Grid item xs={12} sm={6} lg={3}>
                <SoftTypography variant="caption" fontWeight="bold">
                  Project Type *
                </SoftTypography>
                <SoftBox display="flex" alignItems="center" gap={0.5}>
                  <SoftBox flex={1}>
                    <SoftSelect
                      {...compactSoftSelectProps}
                      placeholder="Select Project Type"
                      value={form.project_type_option}
                      options={mergeSelectedOption(
                        bidProjectTypeOptions,
                        form.project_type_option
                      )}
                      isLoading={bidProjectTypeOptionsLoading}
                      isDisabled={isBidDialogReadOnly}
                      onMenuOpen={fetchBidProjectTypeOptions}
                      onChange={(value) => updateForm("project_type_option", value)}
                      isSearchable
                      isClearable
                    />
                  </SoftBox>
                  {canOpenProjectTypeModal && (
                    <Tooltip title="Add project type">
                      <IconButton
                        color="info"
                        size="small"
                        disabled={isBidDialogReadOnly}
                        onClick={() => {
                          setNewBidProjectType("");
                          setBidProjectTypeDialogOpen(true);
                        }}
                        sx={compactFieldActionButtonSx}
                      >
                        <Icon fontSize="small">add</Icon>
                      </IconButton>
                    </Tooltip>
                  )}
                </SoftBox>
              </Grid>
            )}

            {canShowBidDialogField("client_name", "client_code") && (
              <Grid item xs={12} sm={6} lg={3}>
                <SoftTypography variant="caption" fontWeight="bold">
                  Client *
                </SoftTypography>
                <SoftSelect
                  {...compactSoftSelectProps}
                  placeholder="Search client"
                  value={form.client}
                  options={clients}
                  isLoading={clientsLoading}
                  isDisabled={isBidDialogReadOnly}
                  onInputChange={(value) => setClientSearch(value)}
                  onMenuOpen={() => fetchClients(clientSearch)}
                  onChange={(value) => updateForm("client", value)}
                  isSearchable
                  isClearable
                />
              </Grid>
            )}

            {canShowBidDialogField("project_launch_date") && (
              <Grid item xs={12} sm={6} lg={3}>
                <SoftTypography variant="caption" fontWeight="bold">
                  Project Launch Date
                </SoftTypography>
                <SoftInput
                  type="datetime-local"
                  value={form.project_launch_date}
                  disabled={isBidDialogReadOnly}
                  onChange={(event) => updateForm("project_launch_date", event.target.value)}
                />
              </Grid>
            )}

            {canShowBidDialogField("attachment_docs") && (
              <Grid item xs={12} sm={6} lg={3}>
                <SoftTypography variant="caption" fontWeight="bold">
                  Attachment
                </SoftTypography>
                <SoftBox
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={compactAttachmentBoxSx}
                >
                  <SoftBox minWidth={0} flex={1}>
                    {form.attachments.length || isBidDialogCreate ? (
                      <SoftTypography
                        variant="caption"
                        color="text"
                        sx={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                      >
                        {attachmentNames(form.attachments)}
                      </SoftTypography>
                    ) : (
                      renderAttachmentLinks(form.existing_attachments)
                    )}
                  </SoftBox>
                  <SoftButton
                    color="info"
                    variant="outlined"
                    size="small"
                    component="label"
                    disabled={isBidDialogReadOnly}
                    sx={{ ...tabSizedButtonSx, ml: 1 }}
                  >
                    Upload
                    <input
                      type="file"
                      multiple
                      accept={BID_ATTACHMENT_ACCEPT}
                      hidden
                      disabled={isBidDialogReadOnly}
                      onChange={(event) => updateBidAttachments(Array.from(event.target.files || []))}
                    />
                  </SoftButton>
                </SoftBox>
              </Grid>
            )}

            {canShowBidDialogField("comments") && (
              <Grid item xs={12} sm={6} lg={3}>
                <SoftTypography variant="caption" fontWeight="bold">
                  Comments
                </SoftTypography>
                <SoftInput
                  multiline
                  rows={1}
                  value={form.comments}
                  placeholder="Comments"
                  disabled={isBidDialogReadOnly}
                  onChange={(event) => updateForm("comments", event.target.value)}
                  sx={compactTextareaSx}
                />
              </Grid>
            )}
          </Grid>
          {form.details.map((detail, index) => (
            <Fragment key={`bid-detail-${index}`}>
              <SoftBox
                mt={0.375}
                p={0.5}
                border="1px solid #d2d6da"
                borderRadius="6px"
              >
                <SoftBox display="flex" justifyContent="space-between" alignItems="center" mb={0.1875}>
                  <SoftTypography variant="caption" fontWeight="bold" color="text">
                    Country Detail {index + 1}
                  </SoftTypography>
                  {form.details.length > 1 && canRemoveDetailRow && !isBidDialogReadOnly && (
                    <SoftButton color="error" size="small" variant="text" onClick={() => removeDetailRow(index)}>
                      Remove
                    </SoftButton>
                  )}
                </SoftBox>

                <SoftBox sx={countryDetailGridSx}>
                  {canShowBidDialogField("country") && (
                    <SoftBox>
                      <SoftTypography variant="caption" fontWeight="bold">
                        Country/Geo *
                      </SoftTypography>
                      <SoftBox display="flex" alignItems="center" gap={0.5}>
                        <SoftBox flex={1} minWidth={0}>
                          <SoftSelect
                            {...compactSoftSelectProps}
                            placeholder="Select Country/Geo"
                            value={detail.country}
                            options={countries}
                            isLoading={countriesLoading}
                            isDisabled={isBidDialogReadOnly}
                            onMenuOpen={fetchCountries}
                            onChange={(value) => updateDetail(index, "country", value)}
                            isSearchable
                            isClearable
                            formatOptionLabel={formatCountryOptionLabel}
                          />
                        </SoftBox>
                      </SoftBox>
                    </SoftBox>
                  )}

                  {canShowBidDialogField("loi") && (
                    <SoftBox>
                      <SoftTypography variant="caption" fontWeight="bold">
                        LOI(mins)
                      </SoftTypography>
                      <SoftInput
                        type="number"
                        min="0"
                        step="0.01"
                        value={detail.loi}
                        placeholder="LOI(mins)"
                        disabled={isBidDialogReadOnly}
                        onChange={(event) => updateDetail(index, "loi", event.target.value)}
                      />
                    </SoftBox>
                  )}

                  {canShowBidDialogField("ir") && (
                    <SoftBox>
                      <SoftTypography variant="caption" fontWeight="bold">
                        IR(%)
                      </SoftTypography>
                      <SoftInput
                        type="number"
                        min="0"
                        step="0.01"
                        value={detail.ir}
                        placeholder="IR(%)"
                        disabled={isBidDialogReadOnly}
                        onChange={(event) => updateDetail(index, "ir", event.target.value)}
                      />
                    </SoftBox>
                  )}

                  {canShowBidDialogField("bid_cpi") && (
                    <SoftBox>
                      <SoftTypography variant="caption" fontWeight="bold">
                        Bid CPI ($)
                      </SoftTypography>
                      <SoftInput
                        type="text"
                        inputProps={{ inputMode: "decimal", maxLength: 6 }}
                        value={detail.bid_cpi}
                        placeholder="Bid CPI"
                        disabled={isBidDialogReadOnly}
                        onChange={(event) => updateDetail(index, "bid_cpi", event.target.value)}
                      />
                    </SoftBox>
                  )}

                  {isBidDialogWon && canShowBidDialogField("won_cpi") && (
                    <SoftBox>
                      <SoftTypography variant="caption" fontWeight="bold">
                        Won CPI ($)
                      </SoftTypography>
                      <SoftInput
                        type="text"
                        inputProps={{ inputMode: "decimal", maxLength: 6 }}
                        value={detail.won_cpi}
                        placeholder="Won CPI"
                        disabled={isBidDialogReadOnly}
                        onChange={(event) => updateDetail(index, "won_cpi", event.target.value)}
                      />
                    </SoftBox>
                  )}

                  {canShowBidDialogField("required_n") && (
                    <SoftBox>
                      <SoftTypography variant="caption" fontWeight="bold">
                        Required N
                      </SoftTypography>
                      <SoftInput
                        type="text"
                        inputProps={{ inputMode: "numeric", maxLength: 7 }}
                        value={detail.required_n}
                        placeholder="Required N"
                        disabled={isBidDialogReadOnly}
                        onChange={(event) => updateDetail(index, "required_n", event.target.value)}
                      />
                    </SoftBox>
                  )}

                  {canShowBidDialogField("feasibility") && (
                    <SoftBox>
                      <SoftTypography variant="caption" fontWeight="bold">
                        Feasibility
                      </SoftTypography>
                      <SoftInput
                        type="text"
                        inputProps={{ inputMode: "numeric", maxLength: 7 }}
                        value={detail.feasibility}
                        placeholder="Feasibility"
                        disabled={isBidDialogReadOnly}
                        onChange={(event) => updateDetail(index, "feasibility", event.target.value)}
                      />
                    </SoftBox>
                  )}

                  {canShowBidDialogField("required_field_time_days") && (
                    <SoftBox>
                      <SoftTypography variant="caption" fontWeight="bold">
                        Required Field Time (Days)
                      </SoftTypography>
                      <SoftInput
                        type="text"
                        inputProps={{ inputMode: "numeric", maxLength: 7 }}
                        value={detail.required_field_time_days}
                        placeholder="Days"
                        disabled={isBidDialogReadOnly}
                        onChange={(event) =>
                          updateDetail(index, "required_field_time_days", event.target.value)
                        }
                      />
                    </SoftBox>
                  )}

                </SoftBox>
                {canShowBidDialogField("targeting") && (
                  <SoftBox mt={0.375}>
                    <SoftTypography variant="caption" fontWeight="bold">
                      Targeting
                    </SoftTypography>
                    <TargetingEditor
                      value={detail.targeting}
                      onChange={(value) => updateDetail(index, "targeting", value)}
                      readOnly={isBidDialogReadOnly}
                    />
                  </SoftBox>
                )}
              </SoftBox>
              {index === 0 && canAddDetailRow && !isBidDialogReadOnly && (
                <SoftBox mt={0.375} display="flex" justifyContent="flex-end">
                  <SoftButton
                    color="info"
                    size="small"
                    variant="outlined"
                    startIcon={<Icon>add</Icon>}
                    onClick={addDetailRow}
                    sx={tabSizedButtonSx}
                  >
                    Add Country
                  </SoftButton>
                </SoftBox>
              )}
            </Fragment>
          ))}
        </DialogContent>
        <DialogActions sx={compactBidDialogActionsSx}>
          <SoftButton
            color="dark"
            variant="outlined"
            onClick={closeAddDialog}
            disabled={submitLoading}
            sx={tabSizedButtonSx}
          >
            Close
          </SoftButton>
          {canSubmitBidDialog && !isBidDialogReadOnly && (
            <SoftButton
              color="info"
              onClick={handleSubmit}
              disabled={submitLoading}
              endIcon={submitLoading ? <CircularProgress size={20} color="inherit" /> : null}
              sx={tabSizedButtonSx}
            >
              {submitLoading
                ? "Processing..."
                : isBidDialogWon
                  ? "Confirm and Won"
                  : isBidDialogEdit
                    ? "Update Bid"
                    : "Save Bid"}
            </SoftButton>
          )}
        </DialogActions>
      </Dialog>

      <Dialog
        open={wonDialogOpen}
        onClose={closeWonDialog}
        PaperProps={{ sx: responsiveBidDialogPaperSx }}
      >
        <DialogTitle sx={compactBidDialogTitleSx}>
          RFQ To Vendors
        </DialogTitle>
        <DialogContent sx={compactBidDialogContentSx}>
          <Grid container columnSpacing={1.25} rowSpacing={0.375}>
            {canViewField("project_title") && (
            <Grid item xs={12} sm={6} lg={3}>
              <SoftTypography variant="caption" fontWeight="bold">
                Project Title
              </SoftTypography>
              <SoftInput
                value={wonForm.project_title}
                placeholder="Project Title"
                onChange={(event) => updateWonForm("project_title", event.target.value)}
              />
            </Grid>
            )}

            {canViewField("project_manager_role") && (
            <Grid item xs={12} sm={6} lg={3}>
              <SoftTypography variant="caption" fontWeight="bold">
                Project Management Team
              </SoftTypography>
              <SoftSelect
                {...compactSoftSelectProps}
                placeholder="Select Project Management Team"
                value={wonForm.project_manager_role}
                options={projectManagerRoles}
                isLoading={projectManagersLoading}
                onMenuOpen={fetchProjectManagers}
                onChange={(value) => updateWonForm("project_manager_role", value)}
                isSearchable
                isClearable
              />
            </Grid>
            )}

            {canViewField("project_type") && (
            <Grid item xs={12} sm={6} lg={3}>
              <SoftTypography variant="caption" fontWeight="bold">
                Study Type
              </SoftTypography>
              <SoftSelect
                {...compactSoftSelectProps}
                placeholder="Select Study Type"
                value={wonForm.project_type}
                options={projectTypes}
                isLoading={projectTypesLoading}
                onMenuOpen={fetchProjectTypes}
                onChange={(value) => updateWonForm("project_type", value)}
                isSearchable
                isClearable
              />
            </Grid>
            )}

            {canViewField("project_type") && (
            <Grid item xs={12} sm={6} lg={3}>
              <SoftTypography variant="caption" fontWeight="bold">
                Project Type
              </SoftTypography>
              <SoftSelect
                {...compactSoftSelectProps}
                placeholder="Select Project Type"
                value={wonForm.project_type_option}
                options={mergeSelectedOption(
                  bidProjectTypeOptions,
                  wonForm.project_type_option
                )}
                isLoading={bidProjectTypeOptionsLoading}
                onMenuOpen={fetchBidProjectTypeOptions}
                onChange={(value) => updateWonForm("project_type_option", value)}
                isSearchable
                isClearable
              />
            </Grid>
            )}

            {canViewAnyField("client_name", "client_code") && (
            <Grid item xs={12} sm={6} lg={3}>
              <SoftTypography variant="caption" fontWeight="bold">
                Client
              </SoftTypography>
              <SoftSelect
                {...compactSoftSelectProps}
                placeholder="Search client"
                value={wonForm.client}
                options={clients}
                isLoading={clientsLoading}
                onInputChange={(value) => setClientSearch(value)}
                onMenuOpen={() => fetchClients(clientSearch)}
                onChange={(value) => updateWonForm("client", value)}
                isSearchable
                isClearable
              />
            </Grid>
            )}

            {canViewField("project_launch_date") && (
            <Grid item xs={12} sm={6} lg={3}>
              <SoftTypography variant="caption" fontWeight="bold">
                Project Launch Date
              </SoftTypography>
              <SoftInput
                type="datetime-local"
                value={wonForm.project_launch_date}
                onChange={(event) => updateWonForm("project_launch_date", event.target.value)}
              />
            </Grid>
            )}

            {canViewField("attachment_docs") && (
            <Grid item xs={12} sm={6} lg={3}>
              <SoftTypography variant="caption" fontWeight="bold">
                Attachment
              </SoftTypography>
              <SoftBox
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                sx={compactAttachmentBoxSx}
              >
                <SoftBox minWidth={0} flex={1}>
                  {wonAttachments.length ? (
                    <SoftTypography
                      variant="caption"
                      color="text"
                      sx={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                    >
                      {attachmentNames(wonAttachments)}
                    </SoftTypography>
                  ) : (
                    renderAttachmentLinks(wonDialogBid?.attachment_docs)
                  )}
                </SoftBox>
                <SoftButton
                  color="info"
                  variant="outlined"
                  size="small"
                  component="label"
                  sx={{ ...tabSizedButtonSx, ml: 1 }}
                >
                  Upload
                    <input
                      type="file"
                      multiple
                      accept={BID_ATTACHMENT_ACCEPT}
                      hidden
                      onChange={(event) => {
                        const files = Array.from(event.target.files || []);
                        const existingCount = normalizeAttachments(wonDialogBid?.attachment_docs).length;

                        if (validateBidAttachmentFiles(files, existingCount)) {
                          setWonAttachments(files);
                        }
                      }}
                    />
                </SoftButton>
              </SoftBox>
            </Grid>
            )}

            {canViewField("comments") && (
            <Grid item xs={12} sm={6} lg={3}>
              <SoftTypography variant="caption" fontWeight="bold">
                Comments
              </SoftTypography>
              <SoftInput
                multiline
                rows={1}
                value={wonForm.comments}
                placeholder="Comments"
                onChange={(event) => updateWonForm("comments", event.target.value)}
                sx={compactTextareaSx}
              />
            </Grid>
            )}

            {canViewField("vendors") && (
            <Grid item xs={12} sm={6} lg={3}>
              <SoftTypography variant="caption" fontWeight="bold">
                Vendors
              </SoftTypography>
              <SoftSelect
                {...compactSoftSelectProps}
                placeholder="Select vendors"
                value={wonVendors}
                options={vendors}
                isLoading={vendorsLoading}
                isDisabled={isVendorFeasibilityLockedWonMode}
                onMenuOpen={() => {
                  if (!isVendorFeasibilityLockedWonMode && !isVendorFeasibilitySubmittedWonMode) fetchVendors();
                }}
                onChange={(value) => {
                  if (!isVendorFeasibilityLockedWonMode) setWonVendors(value || []);
                }}
                isSearchable
                isClearable={!isVendorFeasibilityLockedWonMode}
                isMulti
              />
            </Grid>
            )}
          </Grid>

          {canViewWonCountryDetails && (
          <>
          {wonDetails.length === 0 ? (
            <SoftBox mt={0.375} p={0.5} border="1px solid #d2d6da" borderRadius="6px">
              <SoftTypography variant="body2" color="text">
                No country details found.
              </SoftTypography>
            </SoftBox>
          ) : (
            wonDetails.map((detail, index) => (
              <SoftBox
                key={detail.id || `won-detail-${index}`}
                mt={0.375}
                p={0.5}
                border="1px solid #d2d6da"
                borderRadius="6px"
              >
                <SoftTypography variant="caption" fontWeight="bold" color="text">
                  Country Detail {index + 1}
                </SoftTypography>

                <SoftBox mt={0.1875} sx={wonCountryDetailGridSx}>
                  {canViewField("country") && (
                  <SoftBox>
                    <SoftTypography variant="caption" fontWeight="bold">
                      Country/Geo
                    </SoftTypography>
                    <SoftSelect
                      {...compactSoftSelectProps}
                      placeholder="Select Country/Geo"
                      value={detail.country}
                      options={countries}
                      isLoading={countriesLoading}
                      onMenuOpen={fetchCountries}
                      onChange={(value) => updateWonDetail(index, "country", value)}
                      isSearchable
                      isClearable
                      formatOptionLabel={formatCountryOptionLabel}
                    />
                  </SoftBox>
                  )}

                  {canViewField("loi") && (
                  <SoftBox>
                    <SoftTypography variant="caption" fontWeight="bold">
                      LOI(mins)
                    </SoftTypography>
                    <SoftInput
                      type="number"
                      min="0"
                      step="0.01"
                      value={detail.loi ?? ""}
                      placeholder="LOI(mins)"
                      onChange={(event) => updateWonDetail(index, "loi", event.target.value)}
                    />
                  </SoftBox>
                  )}

                  {canViewField("ir") && (
                  <SoftBox>
                    <SoftTypography variant="caption" fontWeight="bold">
                      IR(%)
                    </SoftTypography>
                    <SoftInput
                      type="number"
                      min="0"
                      step="0.01"
                      value={detail.ir ?? ""}
                      placeholder="IR(%)"
                      onChange={(event) => updateWonDetail(index, "ir", event.target.value)}
                    />
                  </SoftBox>
                  )}

                  {canViewField("bid_cpi") && (
                  <SoftBox>
                    <SoftTypography variant="caption" fontWeight="bold">
                      Bid CPI ($)
                    </SoftTypography>
                    <SoftInput
                      type="text"
                      inputProps={{ inputMode: "decimal", maxLength: 6 }}
                      value={detail.bid_cpi ?? ""}
                      placeholder="Bid CPI"
                      onChange={(event) => updateWonDetail(index, "bid_cpi", event.target.value)}
                    />
                  </SoftBox>
                  )}

                  {canViewField("won_cpi") && (
                  <SoftBox>
                    <SoftTypography variant="caption" fontWeight="bold">
                      Won CPI ($)
                    </SoftTypography>
                    <SoftInput
                      type="text"
                      inputProps={{ inputMode: "decimal", maxLength: 6 }}
                      value={detail.won_cpi ?? ""}
                      placeholder="Won CPI"
                      onChange={(event) => updateWonDetail(index, "won_cpi", event.target.value)}
                    />
                  </SoftBox>
                  )}

                  {canViewField("required_n") && (
                  <SoftBox>
                    <SoftTypography variant="caption" fontWeight="bold">
                      Required N
                    </SoftTypography>
                    <SoftInput
                      type="text"
                      inputProps={{ inputMode: "numeric", maxLength: 7 }}
                      value={detail.required_n ?? ""}
                      placeholder="Required N"
                      onChange={(event) => updateWonDetail(index, "required_n", event.target.value)}
                    />
                  </SoftBox>
                  )}

                  {canViewField("feasibility") && (
                  <SoftBox>
                    <SoftTypography variant="caption" fontWeight="bold">
                      Feasibility
                    </SoftTypography>
                    <SoftInput
                      type="text"
                      inputProps={{ inputMode: "numeric", maxLength: 7 }}
                      value={detail.feasibility ?? ""}
                      placeholder="Feasibility"
                      onChange={(event) => updateWonDetail(index, "feasibility", event.target.value)}
                    />
                  </SoftBox>
                  )}

                  {canViewField("required_field_time_days") && (
                  <SoftBox>
                    <SoftTypography variant="caption" fontWeight="bold">
                      Required Field Time
                    </SoftTypography>
                    <SoftInput
                      type="text"
                      inputProps={{ inputMode: "numeric", maxLength: 7 }}
                      value={detail.required_field_time_days ?? ""}
                      placeholder="Days"
                      onChange={(event) =>
                        updateWonDetail(index, "required_field_time_days", event.target.value)
                      }
                    />
                  </SoftBox>
                  )}
                </SoftBox>

                {canViewField("targeting") && (
                <SoftBox mt={0.375}>
                  <SoftTypography variant="caption" fontWeight="bold">
                    Targeting
                  </SoftTypography>
                  <TargetingEditor
                    value={detail.targeting || ""}
                    onChange={(value) => updateWonDetail(index, "targeting", value)}
                  />
                </SoftBox>
                )}
              </SoftBox>
            ))
          )}
          </>
          )}
        </DialogContent>
        <DialogActions sx={compactBidDialogActionsSx}>
          <SoftButton
            color="dark"
            variant="outlined"
            onClick={closeWonDialog}
            disabled={statusSubmitLoading}
            sx={tabSizedButtonSx}
          >
            Close
          </SoftButton>
          {canSubmitWonDialog && (
            <SoftButton
              color="success"
              onClick={submitWonBid}
              disabled={statusSubmitLoading}
              endIcon={statusSubmitLoading ? <CircularProgress size={20} color="inherit" /> : null}
              sx={tabSizedButtonSx}
            >
              {statusSubmitLoading ? "Processing..." : "RFQ To Vendors"}
            </SoftButton>
          )}
        </DialogActions>
      </Dialog>

      <Dialog
        open={projectTypeDialogOpen}
        onClose={() => !masterSubmitLoading && setProjectTypeDialogOpen(false)}
        PaperProps={{ sx: responsiveBidDialogPaperSx }}
      >
        <DialogTitle sx={compactBidDialogTitleSx}>Add Study Type</DialogTitle>
        <DialogContent sx={compactBidDialogContentSx}>
          <SoftTypography variant="caption" fontWeight="bold">
            Study Type Name *
          </SoftTypography>
          <SoftInput
            value={newProjectType}
            placeholder="Study Type Name"
            onChange={(event) => setNewProjectType(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter" && canSubmitProjectType) createProjectType();
            }}
          />
        </DialogContent>
        <DialogActions sx={compactBidDialogActionsSx}>
          <SoftButton
            color="dark"
            variant="outlined"
            disabled={masterSubmitLoading}
            onClick={() => setProjectTypeDialogOpen(false)}
            sx={tabSizedButtonSx}
          >
            Close
          </SoftButton>
          {canSubmitProjectType && (
            <SoftButton
              color="info"
              disabled={masterSubmitLoading}
              onClick={createProjectType}
              sx={tabSizedButtonSx}
            >
              {masterSubmitLoading ? "Processing..." : "Save"}
            </SoftButton>
          )}
        </DialogActions>
      </Dialog>

      <Dialog
        open={bidProjectTypeDialogOpen}
        onClose={() => !masterSubmitLoading && setBidProjectTypeDialogOpen(false)}
        PaperProps={{ sx: responsiveBidDialogPaperSx }}
      >
        <DialogTitle sx={compactBidDialogTitleSx}>Add Project Type</DialogTitle>
        <DialogContent sx={compactBidDialogContentSx}>
          <SoftTypography variant="caption" fontWeight="bold">
            Project Type Name *
          </SoftTypography>
          <SoftInput
            value={newBidProjectType}
            placeholder="Project Type Name"
            onChange={(event) => setNewBidProjectType(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter" && canSubmitProjectType) createBidProjectTypeOption();
            }}
          />
        </DialogContent>
        <DialogActions sx={compactBidDialogActionsSx}>
          <SoftButton
            color="dark"
            variant="outlined"
            disabled={masterSubmitLoading}
            onClick={() => setBidProjectTypeDialogOpen(false)}
            sx={tabSizedButtonSx}
          >
            Close
          </SoftButton>
          {canSubmitProjectType && (
            <SoftButton
              color="info"
              disabled={masterSubmitLoading}
              onClick={createBidProjectTypeOption}
              sx={tabSizedButtonSx}
            >
              {masterSubmitLoading ? "Processing..." : "Save"}
            </SoftButton>
          )}
        </DialogActions>
      </Dialog>

    </DashboardLayout>
  );
}

export default BidList;
